import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import Button from "react-bootstrap/esm/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import TextInput from "../TextInput/TextInput";
import TextArea from "../TextArea/TextArea";
// import CustomSelect from '../components/Select/Select';
import { truncateText } from "@/utils/utilFunctions/truncateText";
import { formatEntityName } from "@/utils/utilFunctions/entityName";
import "./PresetStyle.css";
import ChipInput from "../ChipInput/ChipInput";
import {
  generateTeamOptions,
  mediaSocialsToPresetSocialPayload,
} from "@/utils/mediautils/CommonFunctions";
import { RootState, store } from "../../store";
import { getCreditList } from "@/store/media/media.action";
import AiTransformer from "../Transformer/AiTransformer";
import DenmarkPublishDetails from "../MediaPublish/DenmarkPublish/DenmarkPublishDetails";
import { connect, ConnectedProps } from "react-redux";
import {
  CustomSelect,
  PublishedIcon,
  toast,
  UnifiedDateTimePicker,
  SelectedMediaCard,
} from "..";
import { secondsToHms } from "@/utils";
import { Alert } from "@/utils/ui/Alert/Alert";
import { TriangleAlert, Dot } from "lucide-react";
import { AdminPreset, ProductModel } from "@/database";
import { getListUser } from "@/store/user/user.action";
import { getListAgencies } from "@/store/constants/constants.action";
import Modal from "../Modal/Modal";
import AllSocialPublish from "../MediaPublish/SocialPublish/SocialPublish";

import {
  DenmarkPublish as DenmarkPublishInterface,
  MediaModel,
  MediaPreset,
  PresetListModel,
  UserModel,
} from "@/database";
import { getMostFrequentAll } from "@/store/user/user.action";
import { COUNTRIES, PRESET_MODES } from "@/utils/constants/uiConstants";
import { useOptionsFromIds } from "@/hooks/useOptionsFromIds";
import { generateBaseUrl, getThumbnailUrlUtil } from "@/utils/mediaUtils";
import noImage from "../../assets/icons/svg/no-image.svg";

interface RenderCreateMetaPresetProps {
  item?: any;

  mediaDetailConfig?: any;
  onSubmit?: (payload: any) => void;

  onHasChanges?: (hasChanges: boolean) => void;
  isEdit?: boolean;

  selectedMedia?: AdminPreset | null | undefined;
  liveList?: any;
  isPreview?: boolean;
  handleSetMediaData?: (mediaData: any) => void;
  handleNext?: (payload: any) => void;
  onThumbToggle?: (excludedThumbTypes: Set<string>) => void;
  onSocialToggle?: (excludedKeys: Set<string>) => void;
  onPlaylistToggle?: (excludedKeys: Set<string>) => void;
  currentMediaDetailsForEdit?: any;
  readOnlyMode?: boolean;
}

interface Option {
  label: string;
  value: string;
}

const getModeDotClass = (mode?: string) => {
  const normalized = mode?.toString().trim().toLowerCase();

  switch (normalized) {
    case PRESET_MODES.OVERRIDE:
    case "override":
      return "dot dot-override";

    case PRESET_MODES.FILL_IF_EMPTY:
    case "fill_if_empty":
    case "fill if empty":
      return "dot dot-fill-if-empty";

    case PRESET_MODES.APPEND:
    case "append":
      return "dot dot-append";

    default:
      return "";
  }
};

// `media.images` carries non-thumbnail metadata keys (list of raw asset
// uploads, an "images came from a from_url fetch" marker, a "base" key with
// no real thumbnail behind it, etc) alongside the actual thumb-type entries -
// only show/apply these known thumbnail slots, in this display order.
const THUMB_IMAGE_TYPES = ["thumb", "portrait", "social", "podcast", "poster"];

const production_type: Option[] = [
  {
    label: "In House Originals",
    value: "In House Originals",
  },
  {
    label: "Wired",
    value: "Wired",
  },
  {
    label: "AI Generated",
    value: "AI Generated",
  },
  {
    label: "AI Assisted",
    value: "AI Assisted",
  },
];

const content_type: any[] = [
  {
    label: "Interview",
    value: "interview",
  },
  {
    label: "Explainers",
    value: "explainers",
  },
  {
    label: "Package",
    value: "package",
  },
  {
    label: "Story",
    value: "story",
  },
  {
    label: "Bytes",
    value: "bytes",
  },
  {
    label: "VoxPop",
    value: "voxPop",
  },
  {
    label: "Live",
    value: "live",
  },
  {
    label: "Podcast",
    value: "podcast",
  },
  {
    label: "Music",
    value: "music",
  },
  {
    label: "Advertorial",
    value: "advertorial",
  },
  {
    label: "Campaign",
    value: "campaign",
  },
];

const CreateMetaPreset = ({
  item,
  bunchCategoryList,
  bunchLanguageList,
  genre,
  mostFrequentEditor,
  mostFrequentAnchor,
  mostFrequentScriptWriter,
  mostFrequentCameraman,
  mostFrequentUsers,
  creditList,
  onSubmit,
  audioCreditList,
  videoCreditList,
  imageCreditList,
  onHasChanges,
  isEdit,
  selectedMedia,
  liveList,
  isPreview,
  handleSetMediaData,
  handleNext,
  onThumbToggle,
  onSocialToggle,
  onPlaylistToggle,
  currentMediaDetailsForEdit,
  userList,
  readOnlyMode = false,
  bunchProductList,

  getListUser,
  getMostFrequentAll,
  presetList,
  bunchBusinessProductMap,
  defaultTeam,
  getListAgencies,
}: Props) => {
  // True when this preview is showing a media's own metadata (via "Reuse
  // Meta" -> select media) rather than a real saved preset - media has no
  // title/description prefix-suffix concept, so the UI adapts accordingly.
  const isMediaSource = !!(
    item?._isMediaSource || (selectedMedia as any)?._isMediaSource
  );

  // Same source (item vs selectedMedia) pattern used throughout this file -
  // mapMediaToPresetPayload puts the thumbnail fields on whichever of the two
  // actually carries the media data.
  const mediaSourceThumb: any = item?._isMediaSource ? item : selectedMedia;
  // Only the known thumbnail slots (THUMB_IMAGE_TYPES) - `images` also
  // carries other keys (a "base" placeholder with no real thumbnail, list of
  // raw asset uploads, etc) that aren't real, applicable thumbnails.
  const mediaSourceThumbs = useMemo(() => {
    const images = mediaSourceThumb?.images;
    if (!images || typeof images !== "object") return [];
    return THUMB_IMAGE_TYPES.filter((type) => typeof images[type] === "string")
      .map((type) => ({
        type,
        id: images[type] as string,
        url: getThumbnailUrlUtil({
          media: mediaSourceThumb,
          id: images[type],
          source: mediaSourceThumb.source,
          baseUrl: generateBaseUrl(mediaSourceThumb),
        }),
      }))
      .filter((t) => !!t.url);
  }, [mediaSourceThumb]);

  const [subgenres, setSubgenres] = useState<Record<string, any[]>>({});

  const [mainListCredit, setMainListCredit] = useState<any[]>([]);
  const initialValuesRef = useRef<any>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  const [selectedProduct, setSelectedProduct] = useState<any>(null);

  const [autoSelectAgency, setAutoSelectAgency] = useState<any[]>([]);
  const [autoSelectAgencyCredit, setAutoSelectAgencyCredit] = useState<any[]>(
    []
  );

  const [selectedAgency, setSelectedAgency] = useState<any[]>([]);
  const [selectedAgencyCredit, setSelectedAgencyCredit] = useState<any[]>([]);
  const [selectedContributor, setSelectedContributor] = useState<any[]>([]);
  const [selectedCredits, setSelectedCredits] = useState<any[]>([]);
  // Parent Video - same derivation as MediaDetailsForm's selectedParentVideo,
  // built from the source media's parentData so the same SelectedMediaCard
  // renders here. Only `parentid` (the id) is ever written to formik/the
  // saved payload - this is display-only.
  const [selectedParentVideo, setSelectedParentVideo] = useState<any>(null);

  const [selectedSocialChip, setSelectedSocialChip] = useState<any>(null);
  // Channels unchecked from the "Reuse Meta" social chips list - excluded
  // from the borrowed-socials copy in usePresetManagement's handleNext.
  const [excludedSocialKeys, setExcludedSocialKeys] = useState<Set<string>>(
    new Set()
  );
  // Thumb types unchecked from the "Reuse Meta" thumbnails list - excluded
  // from the borrowed-thumbnails upload in usePresetManagement's handleNext.
  // All thumb types are included (checked) by default.
  const [excludedThumbTypes, setExcludedThumbTypes] = useState<Set<string>>(
    new Set()
  );
  // Stable reference across re-renders: AllSocialPublish resets its internal
  // "already auto-opened this preset" guard whenever presetDetails' array
  // identity changes, which would otherwise re-trigger the auto-open effect
  // and add a second, duplicate card for the same channel on every re-render.
  const selectedSocialChipPresetDetails = useMemo(
    () =>
      selectedSocialChip
        ? mediaSocialsToPresetSocialPayload([selectedSocialChip])
        : [],
    [selectedSocialChip]
  );

  // Playlists unchecked from the "Reuse Meta" playlist chips list - excluded
  // from the borrowed-playlists copy in usePresetManagement's handleNext.
  const [excludedPlaylistKeys, setExcludedPlaylistKeys] = useState<Set<string>>(
    new Set()
  );

  const [subCategoryList, setSubCategoryList] = useState<any>([]);

  const languageOptions = useOptionsFromIds(
    bunchLanguageList,
    selectedProduct?.meta?.language
  );

  const categoryOptions = useOptionsFromIds(
    bunchCategoryList,
    selectedProduct?.meta?.category
  );

  const [selectedSections, setSelectedSections] = useState<
    DenmarkPublishInterface[]
  >([]);
  const [currentSelectedSection, setCurrentSelectedSection] = useState<
    DenmarkPublishInterface[]
  >(
    item?.on_platform_distribution?.denmark_sections ||
      selectedMedia?.on_platform_distribution?.denmark_sections ||
      []
  );
  const currentSelectedSectionRef = useRef<DenmarkPublishInterface[]>(
    item?.on_platform_distribution?.denmark_sections ||
      selectedMedia?.on_platform_distribution?.denmark_sections ||
      []
  );
  const [selectedHost, setSelectedHost] = useState<string>(
    defaultTeam?.settings?.host || ""
  );

  // const [updatedLiveList, setUpdatedLiveList] = useState<any[]>([]);

  // State to track which fields are enabled via checkboxes
  const [enabledFields, setEnabledFields] = useState<Record<string, boolean>>({
    thumb: true,
    title: false,
    title_prefix: false,
    title_suffix: false,
    description_prefix: false,
    description_suffix: false,
    desc: false,
    tags: false,
    team: false,
    ctype: false,
    lang: false,
    category: false,
    subcategory: false,
    agency: false,
    agency_credits: false,
    genre: false,
    subgenre: false,
    mfk: false,
    production_type: false,
    denmark: false,
    producer: false,
    editor: false,
    anchor: false,
    script_writer: false,
    cameraman: false,
    photographer: false,
    researcher: false,
    compiler: false,
    curator: false,
    reporter: false,
    author: false,
    imagecredit: false,
    videocredit: false,
    audiocredit: false,
    gct: false,
    gcl: false,
    expiry: false,
    youtube_id: false,
    parentid: false,
    title_ai_type: false,
    title_ai_value: false,
    desc_ai_type: false,
    desc_ai_value: false,
    title_ai_checked: false,
    desc_ai_checked: false,
    meta_team_mode: false,
    content_type_mode: false,
    production_type_mode: false,
    language_mode: false,
    category_mode: false,
    sub_category_mode: false,
    agency_mode: false,
    agency_credits_mode: false,
    content_rating_mode: false,
    producers_mode: false,
    editors_mode: false,
    anchors_mode: false,
    script_writers_mode: false,
    cameramen_mode: false,
    image_credits_mode: false,
    video_credits_mode: false,
    audio_credits_mode: false,
    tags_mode: false,
    // live_channel: false,
  });

  // Store initial enabledFields for comparison
  const [initialEnabledFields, setInitialEnabledFields] = useState<
    Record<string, boolean>
  >({});

  // Helper function to generate hash from form values and enabled fields
  const generateFormHash = (values: any, enabled: Record<string, boolean>) => {
    const relevantData = {
      values: Object.keys(enabled).reduce((acc, key) => {
        if (enabled[key]) {
          acc[key] = values[key];
        }
        return acc;
      }, {} as any),
      enabled: enabled,
    };
    return JSON.stringify(relevantData);
  };

  useEffect(() => {
    const requestKeys = [
      "ctype",
      "producer",
      "editor",
      "anchor",
      "cameraman",
      "script_writer",
      "category",
    ];
    const request = requestKeys.map((key) => ({
      terms: { field: "value", size: 7 },
      key,
      mfu: true,
    }));

    getMostFrequentAll({ requests: request });
  }, [getMostFrequentAll]);

  // useEffect(() => {
  //   getListUser({});
  // }, []);

  // useEffect(() => {
  //   if (liveList) {
  //     const data = liveList?.map((val: any) => {
  //       return {
  //         label: val?.title,
  //         value: val?.id,
  //       };
  //     });
  //     setUpdatedLiveList([...data]);
  //   }
  // }, [liveList]);

  // Helper function to check if there are changes
  const checkForChanges = useCallback(
    (currentValues: any, currentEnabled: Record<string, boolean>) => {
      if (!initialValuesRef.current) return false;

      const currentHash = generateFormHash(currentValues, currentEnabled);
      const initialHash = initialValuesRef.current;

      return currentHash !== initialHash;
    },
    []
  );

  // Helper function to handle checkbox changes
  const handleFieldToggle = (fieldName: string, checked: boolean) => {
    setEnabledFields((prev) => {
      const newEnabled = {
        ...prev,
        [fieldName]: checked,
      };

      // Check for changes after updating enabled fields
      // const hasFormChanges = checkForChanges(formik.values, newEnabled);
      // setHasChanges(hasFormChanges);
      // if (onHasChanges) {
      //   onHasChanges(hasFormChanges);
      // }

      return newEnabled;
    });

    // Clear the field value when unchecked
    // if (!checked) {
    //   formik.setFieldValue(fieldName, '');
    // }
  };

  // Helper function to get only enabled fields for submission
  const getEnabledFieldsPayload = (values: any) => {
    const payload: any = {
      presetName: values.presetName, // Always include preset name
    };

    // Only include fields that are enabled
    Object.keys(enabledFields).forEach((fieldName) => {
      if (
        enabledFields[fieldName] &&
        values[fieldName] !== undefined &&
        values[fieldName] !== ""
      ) {
        // Handle arrays properly - don't include empty arrays
        if (Array.isArray(values[fieldName])) {
          if (values[fieldName].length > 0) {
            payload[fieldName] = values[fieldName];
          }
        } else {
          payload[fieldName] = values[fieldName];
        }
      }
    });

    return payload;
  };

  useEffect(() => {
    if (genre) {
      const list = genre.reduce((acc: Record<string, any[]>, item: any) => {
        const subgenrelist =
          item?.sub_meta?.map((sub: any) => ({
            label: sub.name,
            value: sub.name,
          })) || [];

        acc[item.name] = subgenrelist;
        return acc;
      }, {});

      setSubgenres(list);
    }
  }, [genre]);

  // Initialize enabled fields based on item prop

  const formik = useFormik<MediaPreset>({
    enableReinitialize: true,
    validateOnChange: true,
    initialValues: {
      presetName: item?.name || selectedMedia?.name || "",
      visibility: item?.team || selectedMedia?.team || [],
      status: item?.status || selectedMedia?.status || "",
      title: item?.title_suffix || selectedMedia?.title_suffix || "",
      title_prefix: item?.title_prefix || selectedMedia?.title_prefix || "",
      title_suffix: item?.title_suffix || selectedMedia?.title_suffix || "",
      description_prefix:
        item?.description_prefix || selectedMedia?.description_prefix || "",
      description_suffix:
        item?.description_suffix || selectedMedia?.description_suffix || "",
      title_ai_type:
        item?.title_ai_transform || selectedMedia?.title_ai_transform || "",
      title_ai_value:
        item?.title_custom_prompt || selectedMedia?.title_custom_prompt || "",
      title_ai_checked: !!(
        item?.title_ai_transform || selectedMedia?.title_ai_transform
      ),
      meta_team_mode:
        item?.meta_team_mode || selectedMedia?.meta_team_mode || "",
      content_type_mode:
        item?.content_type_mode || selectedMedia?.content_type_mode || "",
      production_type_mode:
        item?.production_type_mode || selectedMedia?.production_type_mode || "",
      language_mode: item?.language_mode || selectedMedia?.language_mode || "",
      category_mode: item?.category_mode || selectedMedia?.category_mode || "",
      sub_category_mode:
        item?.sub_category_mode || selectedMedia?.sub_category_mode || "",
      agency_mode: item?.agency_mode || selectedMedia?.agency_mode || "",
      agency_credits_mode:
        item?.agency_credits_mode || selectedMedia?.agency_credits_mode || "",
      content_rating_mode:
        item?.content_rating_mode || selectedMedia?.content_rating_mode || "",
      producers_mode:
        item?.contributors?.producers_mode ||
        selectedMedia?.contributors?.producers_mode ||
        "",
      editors_mode:
        item?.contributors?.editors_mode ||
        selectedMedia?.contributors?.editors_mode ||
        "",
      anchors_mode:
        item?.contributors?.anchors_mode ||
        selectedMedia?.contributors?.anchors_mode ||
        "",
      script_writers_mode:
        item?.contributors?.script_writers_mode ||
        selectedMedia?.contributors?.script_writers_mode ||
        "",
      cameramen_mode:
        item?.contributors?.cameramen_mode ||
        selectedMedia?.contributors?.cameramen_mode ||
        "",
      image_credits_mode:
        item?.credits?.image_credits_mode ||
        selectedMedia?.credits?.image_credits_mode ||
        "",
      video_credits_mode:
        item?.credits?.video_credits_mode ||
        selectedMedia?.credits?.video_credits_mode ||
        "",
      audio_credits_mode:
        item?.credits?.audio_credits_mode ||
        selectedMedia?.credits?.audio_credits_mode ||
        "",
      tags_mode: item?.tags_mode || selectedMedia?.tags_mode || "",
      desc: item?.description_suffix || selectedMedia?.description_suffix || "",
      desc_ai_type:
        item?.description_ai_transform ||
        selectedMedia?.description_ai_transform ||
        "",
      desc_ai_value:
        item?.description_custom_prompt ||
        selectedMedia?.description_custom_prompt ||
        "",
      desc_ai_checked: !!(
        item?.description_ai_transform ||
        selectedMedia?.description_ai_transform
      ),
      tags: item?.tags || selectedMedia?.tags || [],
      team: item?.meta_team || selectedMedia?.meta_team || "",
      ctype:
        item?.content_type ||
        item?.ctype ||
        selectedMedia?.content_type ||
        selectedMedia?.ctype ||
        "",
      lang: item?.language || selectedMedia?.language || "",
      category: item?.category || selectedMedia?.category || "",
      subcategory: item?.sub_category || selectedMedia?.sub_category || "",
      agency: item?.agency || selectedMedia?.agency || "",
      agency_credits:
        item?.agency_credits || selectedMedia?.agency_credits || [],
      // genre: item?.media[0]?.genre || selectedMedia?.genre || "",
      // subgenre: item?.media[0]?.subgenre || selectedMedia?.subgenre || "",
      mfk: item?.content_rating || selectedMedia?.content_rating || "",
      production_type:
        item?.production_type || selectedMedia?.production_type || "",
      // Preset objects nest these under contributors/credits
      // (`item.contributors.producers`); a media source (isMediaSource) has
      // them as flat fields instead (`selectedMedia.producer`), matching
      // MediaModel - fall back to the flat shape so choosing a media as the
      // source still populates these.
      producer:
        item?.contributors?.producers ||
        selectedMedia?.contributors?.producers ||
        (selectedMedia as any)?.producer ||
        (item as any)?.producer ||
        [],
      editor:
        item?.contributors?.editors ||
        selectedMedia?.contributors?.editors ||
        (selectedMedia as any)?.editor ||
        (item as any)?.editor ||
        [],
      anchor:
        item?.contributors?.anchors ||
        selectedMedia?.contributors?.anchors ||
        (selectedMedia as any)?.anchor ||
        (item as any)?.anchor ||
        [],
      photographer:
        item?.contributors?.photographers ||
        selectedMedia?.contributors?.photographers ||
        (selectedMedia as any)?.photographer ||
        (item as any)?.photographer ||
        [],
      // researcher/compiler/curator/reporter/author are stored SINGULAR on
      // contributors (mapMediaToPresetPayload puts them at
      // contributors.researcher, not contributors.researchers, unlike every
      // other contributor field) - check both spellings.
      researcher:
        item?.contributors?.researchers ||
        (item?.contributors as any)?.researcher ||
        selectedMedia?.contributors?.researchers ||
        (selectedMedia?.contributors as any)?.researcher ||
        (selectedMedia as any)?.researcher ||
        (item as any)?.researcher ||
        [],
      compiler:
        item?.contributors?.compilers ||
        (item?.contributors as any)?.compiler ||
        selectedMedia?.contributors?.compilers ||
        (selectedMedia?.contributors as any)?.compiler ||
        (selectedMedia as any)?.compiler ||
        (item as any)?.compiler ||
        [],
      curator:
        item?.contributors?.curators ||
        (item?.contributors as any)?.curator ||
        selectedMedia?.contributors?.curators ||
        (selectedMedia?.contributors as any)?.curator ||
        (selectedMedia as any)?.curator ||
        (item as any)?.curator ||
        [],
      reporter:
        item?.contributors?.reporters ||
        (item?.contributors as any)?.reporter ||
        selectedMedia?.contributors?.reporters ||
        (selectedMedia?.contributors as any)?.reporter ||
        (selectedMedia as any)?.reporter ||
        (item as any)?.reporter ||
        [],
      author:
        item?.contributors?.authors ||
        (item?.contributors as any)?.author ||
        selectedMedia?.contributors?.authors ||
        (selectedMedia?.contributors as any)?.author ||
        (selectedMedia as any)?.author ||
        (item as any)?.author ||
        [],
      script_writer:
        item?.contributors?.script_writers ||
        selectedMedia?.contributors?.script_writers ||
        (selectedMedia as any)?.script_writer ||
        (item as any)?.script_writer ||
        [],
      cameraman:
        item?.contributors?.cameramen ||
        selectedMedia?.contributors?.cameramen ||
        (selectedMedia as any)?.cameraman ||
        (item as any)?.cameraman ||
        [],
      imagecredit:
        item?.credits?.image_credits ||
        selectedMedia?.credits?.image_credits ||
        (selectedMedia as any)?.imagecredit ||
        (item as any)?.imagecredit ||
        [],
      videocredit:
        item?.credits?.video_credits ||
        selectedMedia?.credits?.video_credits ||
        (selectedMedia as any)?.videocredit ||
        (item as any)?.videocredit ||
        [],
      audiocredit:
        item?.credits?.audio_credits ||
        selectedMedia?.credits?.audio_credits ||
        (selectedMedia as any)?.audiocredit ||
        (item as any)?.audiocredit ||
        [],

      // Geo restriction only ever comes from a media source (MediaModel has
      // flat gct/gcl fields via MediaSecurity) - a saved preset (`item`,
      // AdminPreset) has no gct/gcl concept.
      gct: (selectedMedia as any)?.gct,
      gcl: (selectedMedia as any)?.gcl || [],
      // Reuse Meta only - same reasoning as gct/gcl above.
      expiry: (selectedMedia as any)?.expiry || 0,
      youtube_id: (selectedMedia as any)?.youtube_id || "",
      parentid: (selectedMedia as any)?.parentid || "",
      denmark:
        item?.on_platform_distribution?.denmark_sections ||
        selectedMedia?.on_platform_distribution?.denmark_sections ||
        [],
      // live_channel:
      //   item?.on_platform_distribution?.live_channels ||
      //   selectedMedia?.on_platform_distribution?.live_channels ||
      //   [],
    },
    validationSchema: Yup.object({
      presetName: Yup.string()
        .required("Preset Name is required")
        .test(
          "unique-preset-name",
          "Same Name is not allowed",
          function (value) {
            if (!value || !presetList || presetList.length === 0) return true;

            const currentVisibility = this.parent.visibility || [];

            // If no visibility is selected, don't check for duplicates
            // if (currentVisibility.length === 0) {
            //   return true;
            // }

            const duplicateExists = presetList.some((presetItem: any) => {
              // Check if names match
              if (presetItem.name !== value) return false;

              // Check if type matches
              if (presetItem.type !== "meta") return false;

              // Skip self in edit mode
              if (isEdit && presetItem.id === item?.id) return false;

              // Check visibility/team match - compare arrays properly
              const itemteam = presetItem.team || [];

              // If item has no team, don't match
              if (itemteam.length === 0) {
                return false;
              }

              // Check if any visibility value matches any team value
              return currentVisibility.some((vis: string) =>
                itemteam.includes(vis)
              );
            });

            return !duplicateExists;
          }
        ),
      status: Yup.string().required("Status is required"),
      visibility: Yup.array().of(Yup.string()).min(1, "Visibility is required"),
    }),
    onSubmit: async (values) => {
      const errors = await formik.validateForm();

      // Set field errors and touched states for all fields with errors
      Object.keys(errors).forEach((field) => {
        const error = errors[field as keyof typeof errors];
        formik.setFieldError(
          field,
          Array.isArray(error) ? error.join(", ") : error
        );
        formik.setFieldTouched(field, true);
      });

      if (Object.keys(errors).length > 0) {
        return;
      }
      const payload = getEnabledFieldsPayload(values);

      const hasMeaningfulFields = hasMeaningfulEnabledFields();

      if (!hasMeaningfulFields) {
        setShowAlert(true);
        // <Alert variant="danger">Please select at least one field</Alert>;
        toast.fail("Please select at least one field");

        return;
      }

      if (
        Object.keys(payload).length === 0 ||
        Object.keys(payload).length === 1 ||
        Object.keys(payload).length === 2
      ) {
        toast.fail("Please select at least one field");

        return;
      }

      if (isEdit && Object.keys(payload).length === 3) {
        toast.fail("Please select at least one field");

        return;
      }

      payload.presetName = values.presetName;
      payload.visibility = values.visibility;
      payload.status = values.status;
      // payload.denmark = currentSelectedSection;
      if (enabledFields.denmark) {
        payload.denmark = currentSelectedSection;

        payload.is_denmark = true;
      } else {
        payload.is_denmark = false;
      }
      if (currentSelectedSection.length === 0) {
        payload.is_denmark = false;
      }
      // Note: live_channel is intentionally not included in presets
      // Presets are templates and should not contain live channel assignments
      // Live channels should be managed directly on media items, not via presets
      // payload.live_channel = values.live_channel;
      payload.title_ai_type = "";
      payload.title_ai_value = "";
      payload.desc_ai_type = "";
      payload.desc_ai_value = "";
      if (payload.title_ai_checked) {
        payload.title_ai_type = values.title_ai_type;
        payload.title_ai_value = values.title_ai_value;
      }
      if (payload.desc_ai_checked) {
        payload.desc_ai_type = values.desc_ai_type;
        payload.desc_ai_value = values.desc_ai_value;
      }
      if (onSubmit) {
        if (isEdit) {
          const data: any = {
            ...payload,
            id: item?.id,
          };
          onSubmit(data);
        } else {
          onSubmit(payload);
        }
        // Reset hash after successful submission
        const newHash = generateFormHash(values, enabledFields);
        initialValuesRef.current = newHash;
        setHasChanges(false);
        if (onHasChanges) {
          onHasChanges(false);
        }
      }
    },
  });

  const createMFUArray = (mfuData: any) => {
    return (
      mfuData?.list
        ?.map((item: any) => {
          const user = userList?.find((user: UserModel) => user.id === item.id);
          if (user) {
            return {
              label: ` ${user.name} ${
                user.email ? "(" + truncateText(user.email, 20) + ")" : ""
              }`,
              value: user.id,
            };
          }
          return null;
        })
        .filter((item: any) => item !== null) || []
    );
  };

  function mapCategoryListToObject(categories: any[]) {
    return categories.reduce(
      (acc, cat) => {
        acc[cat.name] = cat.sub_meta || [];
        return acc;
      },
      {} as Record<string, any[]>
    );
  }

  useEffect(() => {
    if (item || selectedMedia) {
      const initialEnabledFields: Record<string, boolean> = {
        presetName: !!item?.name || !!selectedMedia?.name,
        visibility: !!item?.team || !!selectedMedia?.team,
        status: !!item?.status || !!selectedMedia?.status,
        title:
          !!item?.title_suffix ||
          !!item?.title_prefix ||
          !!selectedMedia?.title_suffix ||
          !!selectedMedia?.title_prefix,
        title_prefix: !!item?.title_prefix || !!selectedMedia?.title_prefix,
        title_suffix: !!item?.title_suffix || !!selectedMedia?.title_suffix,
        description_prefix:
          !!item?.description_prefix || !!selectedMedia?.description_prefix,
        description_suffix:
          !!item?.description_suffix || !!selectedMedia?.description_suffix,
        title_ai_type:
          !!item?.title_ai_transform || !!selectedMedia?.title_ai_transform,
        title_ai_value:
          !!item?.title_custom_prompt || !!selectedMedia?.title_custom_prompt,

        desc:
          !!item?.description_suffix ||
          !!item?.description_prefix ||
          !!selectedMedia?.description_suffix ||
          !!selectedMedia?.description_prefix,
        desc_ai_type:
          !!item?.description_ai_transform ||
          !!selectedMedia?.description_ai_transform,
        desc_ai_value:
          !!item?.description_custom_prompt ||
          !!selectedMedia?.description_custom_prompt,
        tags: !!(
          (item?.tags?.length ?? 0) > 0 ||
          (selectedMedia?.tags?.length ?? 0) > 0
        ),
        team: !!item?.meta_team || !!selectedMedia?.meta_team,
        tags_mode: !!item?.tags_mode || !!selectedMedia?.tags_mode,
        meta_team_mode:
          !!item?.meta_team_mode || !!selectedMedia?.meta_team_mode,
        content_type_mode:
          !!item?.content_type_mode || !!selectedMedia?.content_type_mode,
        production_type_mode:
          !!item?.production_type_mode || !!selectedMedia?.production_type_mode,
        language_mode: !!item?.language_mode || !!selectedMedia?.language_mode,
        category_mode: !!item?.category_mode || !!selectedMedia?.category_mode,
        sub_category_mode:
          !!item?.sub_category_mode || !!selectedMedia?.sub_category_mode,
        agency_mode: !!item?.agency_mode || !!selectedMedia?.agency_mode,
        agency_credits_mode:
          !!item?.agency_credits_mode || !!selectedMedia?.agency_credits_mode,
        content_rating_mode:
          !!item?.content_rating_mode || !!selectedMedia?.content_rating_mode,
        producers_mode:
          !!item?.contributors?.producers_mode ||
          !!selectedMedia?.contributors?.producers_mode,
        editors_mode:
          !!item?.contributors?.editors_mode ||
          !!selectedMedia?.contributors?.editors_mode,
        anchors_mode:
          !!item?.contributors?.anchors_mode ||
          !!selectedMedia?.contributors?.anchors_mode,
        script_writers_mode:
          !!item?.contributors?.script_writers_mode ||
          !!selectedMedia?.contributors?.script_writers_mode,
        cameramen_mode:
          !!item?.contributors?.cameramen_mode ||
          !!selectedMedia?.contributors?.cameramen_mode,
        image_credits_mode:
          !!item?.credits?.image_credits_mode ||
          !!selectedMedia?.credits?.image_credits_mode,
        video_credits_mode:
          !!item?.credits?.video_credits_mode ||
          !!selectedMedia?.credits?.video_credits_mode,
        audio_credits_mode:
          !!item?.credits?.audio_credits_mode ||
          !!selectedMedia?.credits?.audio_credits_mode,
        ctype: !!item?.content_type || !!selectedMedia?.content_type,
        lang: !!item?.language || !!selectedMedia?.language,
        category: !!item?.category || !!selectedMedia?.category,
        subcategory: !!item?.sub_category || !!selectedMedia?.sub_category,
        agency: !!item?.agency || !!selectedMedia?.agency,
        agency_credits: !!(
          (item?.agency_credits?.length ?? 0) > 0 ||
          (selectedMedia?.agency_credits?.length ?? 0) > 0 ||
          []
        ),
        // genre: !!item?.genre || !!selectedMedia?.genre,
        // subgenre: !!item?.subgenre || !!selectedMedia?.subgenre,
        mfk: !!item?.content_rating || !!selectedMedia?.content_rating,
        production_type:
          !!item?.production_type || !!selectedMedia?.production_type,
        producer: !!(
          (item?.contributors?.producers?.length ?? 0) > 0 ||
          (selectedMedia?.contributors?.producers?.length ?? 0) > 0
        ),
        editor: !!(
          (item?.contributors?.editors?.length ?? 0) > 0 ||
          (selectedMedia?.contributors?.editors?.length ?? 0) > 0
        ),
        anchor: !!(
          (item?.contributors?.anchors?.length ?? 0) > 0 ||
          (selectedMedia?.contributors?.anchors?.length ?? 0) > 0
        ),
        script_writer: !!(
          (item?.contributors?.script_writers?.length ?? 0) > 0 ||
          (selectedMedia?.contributors?.script_writers?.length ?? 0) > 0
        ),
        cameraman: !!(
          (item?.contributors?.cameramen?.length ?? 0) > 0 ||
          (selectedMedia?.contributors?.cameramen?.length ?? 0) > 0
        ),
        photographer: !!(
          (item?.contributors?.photographers?.length ?? 0) > 0 ||
          (selectedMedia?.contributors?.photographers?.length ?? 0) > 0
        ),
        researcher: !!(
          (item?.contributors?.researchers?.length ?? 0) > 0 ||
          (selectedMedia?.contributors?.researchers?.length ?? 0) > 0
        ),
        compiler: !!(
          (item?.contributors?.compilers?.length ?? 0) > 0 ||
          (selectedMedia?.contributors?.compilers?.length ?? 0) > 0
        ),
        curator: !!(
          (item?.contributors?.curators?.length ?? 0) > 0 ||
          (selectedMedia?.contributors?.curators?.length ?? 0) > 0
        ),
        reporter: !!(
          (item?.contributors?.reporters?.length ?? 0) > 0 ||
          (selectedMedia?.contributors?.reporters?.length ?? 0) > 0
        ),
        author: !!(
          (item?.contributors?.authors?.length ?? 0) > 0 ||
          (selectedMedia?.contributors?.authors?.length ?? 0) > 0
        ),
        imagecredit: !!(
          (item?.credits?.image_credits?.length ?? 0) > 0 ||
          (selectedMedia?.credits?.image_credits?.length ?? 0) > 0
        ),
        videocredit: !!(
          (item?.credits?.video_credits?.length ?? 0) > 0 ||
          (selectedMedia?.credits?.video_credits?.length ?? 0) > 0
        ),
        audiocredit: !!(
          (item?.credits?.audio_credits?.length ?? 0) > 0 ||
          (selectedMedia?.credits?.audio_credits?.length ?? 0) > 0
        ),
        gct: !!(selectedMedia as any)?.gct,
        gcl: ((selectedMedia as any)?.gcl?.length ?? 0) > 0,
        expiry: !!(selectedMedia as any)?.expiry,
        youtube_id: !!(selectedMedia as any)?.youtube_id,
        parentid: !!(selectedMedia as any)?.parentid,
        title_ai_checked: !!(
          item?.title_ai_transform || selectedMedia?.title_ai_transform
        ),
        desc_ai_checked:
          !!item?.description_ai_transform ||
          !!selectedMedia?.description_ai_transform,
        denmark: Array.isArray(item?.on_platform_distribution?.denmark_sections)
          ? item?.on_platform_distribution?.denmark_sections?.length > 0
          : !!item?.on_platform_distribution?.denmark_sections ||
            !!selectedMedia?.on_platform_distribution?.denmark_sections,
        // live_channel: !!(
        //   (item?.on_platform_distribution?.live_channels?.length ?? 0) > 0 ||
        //   (selectedMedia?.on_platform_distribution?.live_channels?.length ??
        //     0) > 0
        // ),
      };
      setEnabledFields(initialEnabledFields);
      setInitialEnabledFields(initialEnabledFields);
    }
  }, [item, selectedMedia]);

  // Update ref whenever currentSelectedSection changes
  useEffect(() => {
    currentSelectedSectionRef.current = currentSelectedSection;
  }, [currentSelectedSection]);

  useEffect(() => {
    if (bunchProductList) {
      const product = bunchProductList.find(
        (product: ProductModel) => product.id === formik.values.team
      );
      setSelectedProduct(product || null);
    }
  }, [bunchProductList, formik.values.team]);

  useEffect(() => {
    if (bunchCategoryList) {
      const allowedCategoryIds = selectedProduct?.meta?.category;
      if (allowedCategoryIds && allowedCategoryIds.length > 0) {
        const filtered: Record<string, any[]> = {};
        bunchCategoryList.forEach((cat: any) => {
          if (allowedCategoryIds.includes(cat.id)) {
            filtered[cat.name] = cat.sub_meta || [];
          }
        });
        setSubCategoryList(filtered);
      } else {
        setSubCategoryList(mapCategoryListToObject(bunchCategoryList));
      }
    }
  }, [bunchCategoryList]);

  // Create MFU arrays using the helper function
  const mfuEditor = createMFUArray(mostFrequentEditor);
  const mfuAnchor = createMFUArray(mostFrequentAnchor);
  const mfuScriptWriter = createMFUArray(mostFrequentScriptWriter);
  const mfuCameraman = createMFUArray(mostFrequentCameraman);
  const mfuProducer = createMFUArray(mostFrequentUsers);

  interface AiOption {
    key: string;
    value: string;
  }

  const handleTitleAiTypeChange = (payload: AiOption) => {
    formik.setFieldValue("title_ai_type", payload.key);
    formik.setFieldValue("title_ai_value", payload.value);
  };

  const handleDescAiTypeChange = (payload: AiOption) => {
    formik.setFieldValue("desc_ai_type", payload.key);
    formik.setFieldValue("desc_ai_value", payload.value);
  };

  const handleDenmarkchange = (section: any, actionMeta: any) => {
    const newCardId = parseInt(section.value, 10);
    const sectionExists = selectedSections.some(
      (item) =>
        item.section === parseInt(section.label.split("|")[0].trim(), 10)
    );
    const sectionExistsInCurrent = currentSelectedSection.some(
      (item) =>
        item.section === parseInt(section.label.split("|")[0].trim(), 10)
    );
    if (sectionExists || sectionExistsInCurrent) {
      return;
    }

    const updatedDenmaykPublishes: DenmarkPublishInterface[] = [
      ...currentSelectedSection,
    ];
    updatedDenmaykPublishes.unshift({
      section: parseInt(section.label.split("|")[0].trim(), 10),
      article_category: "denmark",
      article_type: "api",
      channel_name: section.label.split("|")[1].trim(),
      host: parseInt(selectedHost, 10),
      folder_path: section.path || "",
      job_type: "denmark",
      upload_type: "api",
      media_id: item?.id || "",
      platform: "denmark",
      tags: item?.tags || [],
      status: 100,
      id: section.value,
      ct: 0,
      mt: 0,
      title: item?.title || "",
      desc: item?.desc || "",
    });

    setCurrentSelectedSection(updatedDenmaykPublishes);
    currentSelectedSectionRef.current = updatedDenmaykPublishes;
  };
  const handlehostChange = (
    newSelectedHost: any,
    _source?: "effect" | "select"
  ) => {
    setSelectedHost(newSelectedHost.value);
  };
  const handleCancelOrDelete = (id: number, type: string) => {
    if (type === "cancel") {
      setCurrentSelectedSection((prevSections) => {
        const updatedSections = prevSections.filter(
          (item) => item.section !== id
        );
        currentSelectedSectionRef.current = updatedSections;
        return updatedSections;
      });
    } else if (type === "delete") {
      // TODO Handle Delete Logic Here
    }
  };

  useEffect(() => {
    if (!isPreview) {
      return;
    }
    if (formik.values) {
      const newEnabledFields = { ...enabledFields };

      // Check each field and update enabled state based on whether it has a value
      Object.keys(formik.values).forEach((fieldName) => {
        if (enabledFields.hasOwnProperty(fieldName)) {
          const value = formik.values[fieldName as keyof MediaPreset];
          let hasValue = false;

          if (Array.isArray(value)) {
            hasValue = value.length > 0;
          } else if (typeof value === "string") {
            hasValue = value.trim() !== "";
          } else if (typeof value === "number") {
            hasValue = value !== 0;
          } else {
            hasValue = !!value;
          }

          newEnabledFields[fieldName] = hasValue;
        }
      });

      setEnabledFields(newEnabledFields);
    }
  }, [formik.values]);

  console.log("this is new data", item, selectedMedia);

  const getBasePermission = (permission: string): string => {
    return permission;
  };

  function processPermissionForSelect(array: string[]) {
    const newarray = array?.map((str) => {
      return getBasePermission(str);
    });
    return newarray;
  }

  const handlePermissionChange = (values: string[], key: string) => {
    const newProducers = values?.map((value) => value);

    formik.setFieldValue(key, newProducers);
  };

  useEffect(() => {
    if (!item && !selectedMedia) return;

    // Same item-vs-selectedMedia fallback pattern used throughout this file -
    // a preset carries these on `item`, a media source (isMediaSource) on
    // `selectedMedia` (or `item` itself, when item._isMediaSource).
    const agencydata = item?.agencydata || (selectedMedia as any)?.agencydata;
    const agencyCreditData =
      item?.agencyCreditData || (selectedMedia as any)?.agencyCreditData;
    const userContributorData =
      item?.userContributorData || (selectedMedia as any)?.userContributorData;
    const creditsData =
      item?.creditsData || (selectedMedia as any)?.creditsData;

    const tempAgency = agencydata
      ? [
          {
            label: formatEntityName(agencydata),
            value: agencydata?.id,
          },
        ]
      : [];
    const tempAgencyCredit =
      agencyCreditData?.map((d: any) => {
        return {
          label: formatEntityName(d),
          value: d?.id,
        };
      }) || [];
    const tempContributor =
      userContributorData?.map((d: any) => {
        return {
          label: ` ${d?.name}  ${
            d?.email ? "(" + truncateText(d?.email, 20) + ")" : ""
          } `,
          value: d?.id,
        };
      }) || [];
    const tempCredits =
      creditsData?.map((d: any) => {
        return {
          label: ` ${d?.name}  ${
            d?.email ? "(" + truncateText(d?.email, 20) + ")" : ""
          } `,
          value: d?.id,
        };
      }) || [];
    setSelectedAgency(tempAgency);
    setSelectedAgencyCredit(tempAgencyCredit);
    setSelectedContributor(tempContributor);
    setSelectedCredits(tempCredits);
  }, [item, selectedMedia]);

  // Same derivation as MediaDetailsForm's selectedParentVideo effect - builds
  // the SelectedMediaCard-shaped object from the source media's parentData.
  useEffect(() => {
    const parentData = (selectedMedia as any)?.parentData;
    if (!parentData) {
      setSelectedParentVideo(null);
      return;
    }

    setSelectedParentVideo({
      label: parentData.id,
      value: parentData.title,
      image: parentData.images?.thumb
        ? getThumbnailUrlUtil({
            media: parentData,
            id: parentData.images.thumb,
            source: parentData?.source,
          })
        : "",
      duration: secondsToHms(parentData.meta?.duration),
      thumb: !!generateBaseUrl(parentData),
    });
  }, [(selectedMedia as any)?.parentData]); // eslint-disable-line react-hooks/exhaustive-deps

  const deepEqual = (a: any, b: any): boolean => {
    if (a === b) return true;
    if (a == null || b == null) return a === b;
    if (typeof a !== typeof b) return false;

    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      return a.every((val, index) => deepEqual(val, b[index]));
    }

    if (typeof a === "object") {
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);
      if (keysA.length !== keysB.length) return false;
      return keysA.every((key) => deepEqual(a[key], b[key]));
    }

    return a === b;
  };

  const hasChangesFuntion = useCallback(() => {
    const initialValues = formik.initialValues;
    const currentValues = formik.values;

    // Helper function to deep compare values

    // Check for changes in basic fields (presetName, status, visibility)
    const basicFields = ["presetName", "status", "visibility"];
    const basicFieldsHaveChanges = basicFields.some((field) => {
      const initial = initialValues[field as keyof MediaPreset];
      const current = currentValues[field as keyof MediaPreset];
      return !deepEqual(initial, current);
    });

    // For edit mode, we need to be more careful about the comparison
    // Only check fields that are actually being used in the form
    const formHasChanges = Object.keys(initialValues).some((key) => {
      const initial = initialValues[key as keyof MediaPreset];
      const current = currentValues[key as keyof MediaPreset];

      // Skip comparison for fields that are empty in both initial and current
      if (!initial && !current) return false;

      // For arrays, check if they're both empty
      if (Array.isArray(initial) && Array.isArray(current)) {
        if (initial.length === 0 && current.length === 0) return false;
      }

      const hasChange = !deepEqual(initial, current);

      return hasChange;
    });

    const initialDenmark =
      item?.on_platform_distribution?.denmark_sections ||
      selectedMedia?.on_platform_distribution?.denmark_sections ||
      [];

    const denmarkHasChanges = !deepEqual(
      initialDenmark,
      currentSelectedSection
    );

    // Check if enabledFields have changed
    const enabledFieldsHaveChanged = !deepEqual(
      initialEnabledFields,
      enabledFields
    );

    const result =
      basicFieldsHaveChanges ||
      formHasChanges ||
      denmarkHasChanges ||
      enabledFieldsHaveChanged;

    return result;
  }, [
    formik.initialValues,
    formik.values,
    currentSelectedSection,
    isEdit,
    initialEnabledFields,
    enabledFields,
  ]);

  // Track if form has been initialized to prevent false positives on initial load
  const [isFormInitialized, setIsFormInitialized] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState<any>(null);
  const [initializationDelay, setInitializationDelay] = useState(false);
  const [editFormReady, setEditFormReady] = useState(false);

  // Helper function to check if there are meaningful enabled fields
  const hasMeaningfulEnabledFields = useCallback(() => {
    const basicFields = ["presetName", "visibility", "status"];
    const meaningfulFields = Object.keys(enabledFields).filter((field) => {
      const fieldValue = formik.values[field as keyof MediaPreset];

      // Check if field is enabled
      if (!enabledFields[field]) {
        return false;
      }

      // For basic fields, check if they have meaningful values
      if (basicFields.includes(field)) {
        if (field === "presetName") {
          return (
            fieldValue &&
            typeof fieldValue === "string" &&
            fieldValue.trim() !== ""
          );
        }
        if (field === "status") {
          return fieldValue && fieldValue !== "";
        }
        if (field === "visibility") {
          return Array.isArray(fieldValue) && fieldValue.length > 0;
        }
        return false;
      }

      if (field === "denmark") {
        return currentSelectedSectionRef.current.length > 0;
      }

      // Check if field has a meaningful value based on its type
      if (fieldValue === undefined || fieldValue === null) {
        return false;
      }

      if (Array.isArray(fieldValue)) {
        return fieldValue.length > 0;
      }

      if (typeof fieldValue === "string") {
        return fieldValue.trim() !== "";
      }

      if (typeof fieldValue === "number") {
        return fieldValue !== 0;
      }

      // For boolean values, consider them meaningful if they're true
      if (typeof fieldValue === "boolean") {
        return fieldValue === true;
      }

      // For other types, consider them meaningful if they exist
      return !!fieldValue;
    });

    return meaningfulFields.length > 0;
  }, [enabledFields, formik.values]);

  useEffect(() => {
    // Only check for changes after the form has been properly initialized
    if (isFormInitialized && onHasChanges && initializationDelay) {
      // For edit mode, wait until the form is fully populated
      if (isEdit && !editFormReady) {
        return;
      }

      const hasFormChanges = hasChangesFuntion();
      const hasMeaningfulFields = hasMeaningfulEnabledFields();

      // Pass both the form changes and meaningful fields status
      onHasChanges(hasFormChanges && hasMeaningfulFields);
    }
  }, [
    formik.values,
    formik.initialValues,
    onHasChanges,
    isFormInitialized,
    hasChangesFuntion,
    initializationDelay,
    isEdit,
    editFormReady,
    hasMeaningfulEnabledFields,
    currentSelectedSection,
  ]);

  useEffect(() => {
    const initialDenmark =
      item?.on_platform_distribution?.denmark_sections ||
      selectedMedia?.on_platform_distribution?.denmark_sections ||
      [];
    const denmarkHasChanges = !deepEqual(
      initialDenmark,
      currentSelectedSection
    );

    if (denmarkHasChanges) {
      if (onHasChanges) {
        onHasChanges(true);
      }
    }
  }, [currentSelectedSection]);

  // Mark form as initialized after initial values are set and store initial values
  useEffect(() => {
    if (
      formik.values &&
      Object.keys(formik.values).length > 0 &&
      !isFormInitialized
    ) {
      setIsFormInitialized(true);
      // Store the initial form values for better comparison
      setInitialFormValues(JSON.parse(JSON.stringify(formik.values)));

      // Add a small delay to ensure form is fully populated before checking changes
      setTimeout(() => {
        setInitializationDelay(true);
      }, 100);
    }
  }, [formik.values, isFormInitialized]);

  // Reset initialization when item changes (for editing different presets)
  useEffect(() => {
    if (item) {
      setIsFormInitialized(false);
      setInitialFormValues(null);
      setInitializationDelay(false);
      setEditFormReady(false);
    }
  }, [item?.id]); // Only reset when the item ID changes

  // Mark edit form as ready when all required fields are populated
  useEffect(() => {
    if (
      isEdit &&
      item &&
      formik.values &&
      Object.keys(formik.values).length > 0
    ) {
      // Check if the form has been populated with the item data
      const hasRequiredFields =
        formik.values.presetName && formik.values.status;
      if (hasRequiredFields) {
        setTimeout(() => {
          setEditFormReady(true);
        }, 200);
      }
    }
  }, [isEdit, item, formik.values]);

  const handleCreateMetaPreset = useCallback(() => {
    formik.handleSubmit();
  }, [formik, enabledFields]);

  useEffect(() => {
    window.addEventListener("createMetaPreset", handleCreateMetaPreset);
    return () =>
      window.removeEventListener("createMetaPreset", handleCreateMetaPreset);
  }, [handleCreateMetaPreset]);

  const handlePreviewNextClickMetaPreset = () => {
    const payload = getEnabledFieldsPayload(formik.values);
    if (isMediaSource) {
      payload._isMediaSource = true;
    }

    // `formik.values.denmark` only reflects the value at mount/reinitialize -
    // section add/remove in the preview only updates `currentSelectedSection`
    // (see handleDenmarkchange/handleCancelOrDelete), so use that as the
    // source of truth here, same as the real preset-submit payload does.
    if (enabledFields.denmark && currentSelectedSection.length > 0) {
      payload.denmark = currentSelectedSection;
    } else {
      delete payload.denmark;
    }

    if (formik.values.title_ai_checked) {
      payload.title_ai_type = formik.values.title_ai_type;
      payload.title_ai_value = formik.values.title_ai_value;
    } else {
      payload.title_ai_type = "";
      payload.title_ai_value = "";
    }
    if (formik.values.desc_ai_checked) {
      payload.desc_ai_type = formik.values.desc_ai_type;
      payload.desc_ai_value = formik.values.desc_ai_value;
    } else {
      payload.desc_ai_type = "";
      payload.desc_ai_value = "";
    }

    if (handleSetMediaData) {
      handleSetMediaData(payload);
    }
    if (handleNext) {
      handleNext(true);
    }
  };

  useEffect(() => {
    window.addEventListener(
      "previewNextClickMetaPreset",
      handlePreviewNextClickMetaPreset
    );
    return () =>
      window.removeEventListener(
        "previewNextClickMetaPreset",
        handlePreviewNextClickMetaPreset
      );
  }, [handlePreviewNextClickMetaPreset]);

  // Helper function to check if a field should be shown in preview mode
  const shouldShowField = (fieldValue: any, isPreview: boolean): boolean => {
    if (!isPreview) return true;
    if (Array.isArray(fieldValue)) {
      return fieldValue.length > 0;
    }
    if (typeof fieldValue === "string") {
      return fieldValue.length > 0;
    }
    if (typeof fieldValue === "number") {
      return fieldValue > 0;
    }
    return !!fieldValue;
  };

  // Helper function to check if geo restriction section should be shown
  const shouldShowGeoRestriction = (): boolean => {
    return (
      shouldShowField(formik.values.gct, !!isPreview) ||
      shouldShowField(formik.values.gcl, !!isPreview)
    );
  };

  const shouldShowCredit = (): boolean => {
    return (
      shouldShowField(formik.values.audiocredit, !!isPreview) ||
      shouldShowField(formik.values.videocredit, !!isPreview) ||
      shouldShowField(formik.values.imagecredit, !!isPreview)
    );
  };

  const shouldShowContributors = (): boolean => {
    return (
      shouldShowField(formik.values.editor, !!isPreview) ||
      shouldShowField(formik.values.anchor, !!isPreview) ||
      shouldShowField(formik.values.cameraman, !!isPreview) ||
      shouldShowField(formik.values.script_writer, !!isPreview) ||
      shouldShowField(formik.values.producer, !!isPreview)
      // shouldShowField(formik.values.photographer, !!isPreview) ||
      // shouldShowField(formik.values.researcher, !!isPreview) ||
      // shouldShowField(formik.values.compiler, !!isPreview) ||
      // shouldShowField(formik.values.curator, !!isPreview) ||
      // shouldShowField(formik.values.reporter, !!isPreview) ||
      // shouldShowField(formik.values.author, !!isPreview)
    );
  };

  // Helper function to check if there are meaningful enabled fields (not just presetName)
  // This ensures the create button is only enabled when there are actual content fields enabled,
  // not just the basic preset configuration fields
  // const hasMeaningfulEnabledFields = useCallback(() => {
  //   const meaningfulFields = Object.keys(enabledFields).filter(
  //     (field) => field !== 'presetName' && enabledFields[field],
  //   );
  //   return meaningfulFields.length > 0;
  // }, [enabledFields]);

  return (
    <>
      {isPreview && !readOnlyMode && !isMediaSource && (
        <>
          {/* <Alert
            variant="warning"
            className="py-2 d-flex align-items-center position-sticky z-2"
            style={{ top: "-14px" }}
          > */}
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <span className="legend-chip legend-override">
              <span className="dot dot-override" /> Override
            </span>
            <span className="legend-chip legend-fill-if-empty">
              <span className="dot dot-fill-if-empty" /> Fill if empty
            </span>
            <span className="legend-chip legend-append">
              <span className="dot dot-append" /> Append
            </span>
          </div>
          {/* </Alert> */}
          <div
            className=" d-flex justify-content-center align-items-center  py-1 px-1 H"
            style={{
              backgroundColor: "#FFF3CD",
              width: "fit-content",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            <span
              className="ms-1 align-middle text-warning"
              style={{ fontSize: "35px", lineHeight: 1 }}
            >
              &#8226;
            </span>
            <span>
              You have made changes in highlightes fields. Applying this preset
              will replace your changes.
            </span>
          </div>
        </>
      )}
      {!isPreview && (
        <div
          className="row bg-light p-3 position-sticky z-2 m-0"
          style={{ top: "-16px" }}
        >
          <div className="col-md-4">
            <TextInput
              label="Preset Name"
              name="presetName"
              value={formik?.values?.presetName || ""}
              onChange={(e: any) => {
                formik.setFieldValue("presetName", e.target.value);
              }}
              onBlur={formik.handleBlur}
              showRequired={true}
              placeholder="Enter Preset Name"
              error={formik.errors.presetName as string}
              touched={formik.touched.presetName as boolean}
            />
          </div>

          <div className="col-md-4">
            <CustomSelect
              // value={formik.values.visibility}
              value={processPermissionForSelect(
                formik?.values?.visibility || []
              )}
              // value={prevVideoCredit}
              handleChange={(newValue: any) => {
                handlePermissionChange(
                  newValue.map((option: any) => option.value),
                  "visibility"
                );
              }}
              label="team"
              multiSelect={true}
              placeholder="Team/Property"
              checkBox={true}
              showRequired={true}
              options={
                bunchProductList
                  ?.filter(
                    (item: any) =>
                      item?.access === "admin" || item?.access === "write"
                  )
                  ?.map((item: any) => ({
                    label: formatEntityName(item),
                    value: item?.id,
                  })) || []
              }
              updatedOptions={
                bunchProductList
                  ?.filter(
                    (item: any) =>
                      item?.access === "admin" || item?.access === "write"
                  )
                  ?.map((item: any) => ({
                    label: formatEntityName(item),
                    value: item?.id,
                  })) || []
              }
              hideArrow={true}
              error={formik.errors.visibility as string}
              touched={formik.touched.visibility as boolean}
              handleBlur={formik.handleBlur}
            />
          </div>
          <div className="col-md-4">
            <CustomSelect
              value={formik?.values?.status || ""}
              handleChange={(newValue: any) => {
                formik.setFieldValue("status", newValue.value);
              }}
              label="Status"
              multiSelect={false}
              placeholder="Select Status"
              showRequired={true}
              options={[
                {
                  label: "Active",
                  value: 1,
                },
                {
                  label: "Inactive",
                  value: 2,
                },
              ]}
              updatedOptions={[
                { label: "Active", value: "1" },
                { label: "Inactive", value: "2" },
              ]}
              hideArrow={true}
              error={formik.errors.status as string}
              touched={formik.touched.status as boolean}
              handleBlur={formik.handleBlur}
            />
          </div>
        </div>
      )}

      <div className="presetContainer">
        <div className="row m-0 gy-4">
          {isMediaSource
            ? !(isPreview && !((formik?.values?.title || "")?.length > 0)) && (
                <div className="col-md-12">
                  <TextInput
                    label="Title"
                    name="title"
                    value={formik?.values?.title || ""}
                    onChange={() => {}}
                    onBlur={() => {}}
                    showRequired={false}
                    disabled={true}
                    labelCheckbox={true}
                    labelCheckboxChecked={enabledFields.title_suffix}
                    onLabelCheckboxChange={(checked: boolean) => {
                      handleFieldToggle("title", checked);
                      handleFieldToggle("title_suffix", checked);
                    }}
                    disableLabelCheckbox={
                      isPreview && !((formik?.values?.title || "").length > 0)
                    }
                    readOnlyMode={readOnlyMode}
                  />
                </div>
              )
            : !(isPreview && !((formik?.values?.title || "")?.length > 0)) && (
                <div className="col-md-12 position-relative border pt-4 pb-2">
                  <label
                    className="d-flex gap-2 position-absolute"
                    style={{ top: "-7px" }}
                  >
                    {/* {!readOnlyMode && (
                  <input
                    type="checkbox"
                    checked={enabledFields.title}
                    onChange={(e) => handleFieldToggle('title', e.target.checked)}
                    disabled={isPreview && !((formik?.values?.title || '')?.length > 0)}
                    className="align-middle"
                  />
                )}{' '} */}
                    Title
                  </label>
                  <div className="row ms-2">
                    <div className="col-md-6">
                      <TextInput
                        label="Prefix Text"
                        name="title_prefix"
                        placeholder="Enter prefix title here..."
                        value={formik?.values?.title_prefix || ""}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        showRequired={false}
                        error={formik.errors.title_prefix as string}
                        disabled={isPreview || !enabledFields.title_prefix}
                        labelCheckbox={true}
                        labelCheckboxChecked={enabledFields.title_prefix}
                        onLabelCheckboxChange={(checked: boolean) => {
                          handleFieldToggle("title_prefix", checked);
                        }}
                        disableLabelCheckbox={
                          isPreview && !(formik.values.title_prefix.length > 0)
                        }
                        readOnlyMode={readOnlyMode}
                      />
                    </div>
                    <div className="col-md-6">
                      <TextInput
                        label="Postfix Text"
                        name="title_suffix"
                        placeholder="Enter suffix title here..."
                        value={formik?.values?.title_suffix || ""}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        showRequired={false}
                        error={formik.errors.title_suffix as string}
                        disabled={isPreview || !enabledFields.title_suffix}
                        labelCheckbox={true}
                        labelCheckboxChecked={enabledFields.title_suffix}
                        onLabelCheckboxChange={(checked: boolean) => {
                          handleFieldToggle("title_suffix", checked);
                        }}
                        disableLabelCheckbox={
                          isPreview && !(formik.values.title_suffix.length > 0)
                        }
                        readOnlyMode={readOnlyMode}
                      />
                    </div>
                  </div>
                  {(!isPreview ||
                    (isPreview && formik.values.title_ai_type)) && (
                    <div className="ms-2">
                      <AiTransformer
                        type={formik.values.title_ai_type}
                        defaultValue={formik.values.title_ai_value}
                        onSelect={handleTitleAiTypeChange}
                        item={item}
                        disabled={isPreview}
                        isChecked={enabledFields.title_ai_checked}
                        handleCheckboxChange={(value: any) => {
                          handleFieldToggle("title_ai_checked", value);
                          formik.setFieldValue("title_ai_checked", value);
                        }}
                        readOnlyMode={readOnlyMode}
                        labelType="title"
                        isPreview={isPreview}
                      />
                    </div>
                  )}
                </div>
              )}

          {isMediaSource
            ? !(isPreview && !((formik?.values?.desc || "")?.length > 0)) && (
                <div className="col-md-12">
                  <TextArea
                    label="Description"
                    name="desc"
                    value={formik?.values?.desc || ""}
                    onChange={() => {}}
                    onBlur={() => {}}
                    showRequired={false}
                    disabled={true}
                    labelCheckbox={true}
                    labelCheckboxChecked={enabledFields.description_suffix}
                    onLabelCheckboxChange={(checked: boolean) => {
                      handleFieldToggle("desc", checked);
                      handleFieldToggle("description_suffix", checked);
                    }}
                    disableLabelCheckbox={
                      isPreview && !((formik?.values?.desc || "").length > 0)
                    }
                    readOnlyMode={readOnlyMode}
                  />
                </div>
              )
            : !(isPreview && !((formik?.values?.desc || "")?.length > 0)) && (
                <div className="col-md-12 position-relative border pt-4 pb-2">
                  <label
                    className="d-flex gap-2 position-absolute"
                    style={{ top: "-7px" }}
                  >
                    {/* {!readOnlyMode && (
                  <input
                    type="checkbox"
                    className="align-middle"
                    checked={enabledFields.desc}
                    onChange={(e) => handleFieldToggle('desc', e.target.checked)}
                    disabled={isPreview && !((formik?.values?.desc || '')?.length > 0)}
                  />
                )}{' '} */}
                    DESCRIPTION
                  </label>
                  <div className="row ms-2">
                    <div className="col-md-6">
                      <TextInput
                        label="Prefix Text"
                        name="description_prefix"
                        placeholder="Enter prefix description here..."
                        value={formik?.values?.description_prefix || ""}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        showRequired={false}
                        error={formik.errors.description_prefix as string}
                        disabled={
                          isPreview || !enabledFields.description_prefix
                        }
                        labelCheckbox={true}
                        labelCheckboxChecked={enabledFields.description_prefix}
                        onLabelCheckboxChange={(checked: boolean) => {
                          handleFieldToggle("description_prefix", checked);
                        }}
                        disableLabelCheckbox={
                          isPreview &&
                          !(formik.values.description_prefix.length > 0)
                        }
                        readOnlyMode={readOnlyMode}
                      />
                    </div>
                    <div className="col-md-6">
                      <TextInput
                        label="Postfix Text"
                        name="description_suffix"
                        placeholder="Enter suffix description here..."
                        value={formik?.values?.description_suffix || ""}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        showRequired={false}
                        error={formik.errors.description_suffix as string}
                        disabled={
                          isPreview || !enabledFields.description_suffix
                        }
                        labelCheckbox={true}
                        labelCheckboxChecked={enabledFields.description_suffix}
                        onLabelCheckboxChange={(checked: boolean) => {
                          handleFieldToggle("description_suffix", checked);
                        }}
                        disableLabelCheckbox={
                          isPreview &&
                          !(formik.values.description_suffix.length > 0)
                        }
                        readOnlyMode={readOnlyMode}
                      />
                    </div>
                  </div>
                  {(!isPreview ||
                    (isPreview && formik.values.desc_ai_type)) && (
                    <div className="ms-2">
                      <AiTransformer
                        type={formik.values.desc_ai_type}
                        defaultValue={formik.values.desc_ai_value}
                        onSelect={handleDescAiTypeChange}
                        item={item}
                        disabled={isPreview}
                        isPreview={isPreview}
                        isChecked={enabledFields.desc_ai_checked}
                        readOnlyMode={readOnlyMode}
                        handleCheckboxChange={(value: any) => {
                          handleFieldToggle("desc_ai_checked", value);
                          formik.setFieldValue("desc_ai_checked", value);
                        }}
                        labelType="desc"
                      />
                    </div>
                  )}
                </div>
              )}
          {/* <label className="checkbox-label">
          <input type="checkbox" className="fs-6" /> TAGS
        </label> */}
          {!(isPreview && !((formik?.values?.tags || [])?.length > 0)) && (
            <div className="col-md-12 col-sm-12">
              <ChipInput
                readOnlyMode={readOnlyMode}
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.tags}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle("tags", checked);
                }}
                showRequired={false}
                showAsterisk={!!selectedMedia?.tags_mode || !!item?.tags_mode}
                asteriskClassName={getModeDotClass(
                  selectedMedia?.tags_mode || item?.tags_mode || ""
                )}
                label="Tags"
                value={formik?.values?.tags || []}
                placeholder="Type here to add tags"
                handleChipChange={(chip: string | string[], action: string) => {
                  const currentTags = [...(formik?.values?.tags || [])];
                  let updatedTags = [...currentTags];

                  if (action === "add") {
                    // Handle both single chip and array of chips
                    const chipsToAdd = Array.isArray(chip) ? chip : [chip];
                    updatedTags = [...new Set([...currentTags, ...chipsToAdd])];
                  } else if (action === "remove") {
                    updatedTags = currentTags.filter((t) => t !== chip);
                  } else if (action === "clear") {
                    updatedTags = [];
                  }
                  formik.setFieldValue("tags", updatedTags, true);
                  formik.setFieldTouched("tags", true, false);
                }}
                touched={formik.touched.tags as boolean}
                // error={formik.errors.tags}
                error={
                  formik.errors.tags && typeof formik.errors.tags === "string"
                    ? formik.errors.tags
                    : undefined
                }
                onBlur={() => formik.setFieldTouched("tags", true)}
                disabled={isPreview || !enabledFields.tags}
                disableLabelCheckbox={
                  isPreview && !(formik.values.tags.length > 0)
                }
                // showAsterisk={currentMediaDetailsForEdit?.tags?.length > 0 && enabledFields.tags}

                // disabled={!enabledFields.tags}
              />
            </div>
          )}

          {!(isPreview && !(formik.values.denmark.length > 0)) && (
            <div className="col-md-12 col-sm-12">
              <DenmarkPublishDetails
                selectedHost={selectedHost}
                currentSelectedSection={currentSelectedSection}
                handleDenmarkchange={handleDenmarkchange}
                handlehostChange={handlehostChange}
                handleCancelOrDelete={handleCancelOrDelete}
                selectedSections={selectedSections}
                isPreview={isPreview}
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.denmark}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle("denmark", checked);
                }}
                disabled={isPreview || !enabledFields.denmark}
                readOnlyMode={readOnlyMode}
                isPreset={true}
              />
            </div>
          )}

          {(() => {
            const allMediaSocials =
              item?.socials || (selectedMedia as any)?.socials || [];
            if (
              !Array.isArray(allMediaSocials) ||
              allMediaSocials.length === 0
            ) {
              return null;
            }

            // Only show channels not already published on the media being
            // edited - previewing an already-published channel would just
            // duplicate an existing social publish card.
            const existingChannelKeys = new Set(
              (currentMediaDetailsForEdit?.socials || []).map(
                (social: any) => `${social?.channel_name}::${social?.job_type}`
              )
            );
            const mediaSocials = allMediaSocials.filter(
              (social: any) =>
                social?.job_type !== "denmark" &&
                !existingChannelKeys.has(
                  `${social?.channel_name}::${social?.job_type}`
                )
            );

            if (mediaSocials.length === 0) {
              return null;
            }

            return (
              <div className="col-md-12 col-sm-12">
                <label className="mb-2">Social Channels</label>
                <div className="d-flex flex-wrap gap-2">
                  {mediaSocials.map((social: any, idx: number) => {
                    const socialKey = `${social?.channel_name}::${social?.job_type}`;
                    const isChecked = !excludedSocialKeys.has(socialKey);
                    return (
                      <div
                        key={`${social?.id || social?.channel_name}-${idx}`}
                        className="d-flex align-items-center gap-1"
                      >
                        {!readOnlyMode && (
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setExcludedSocialKeys((prev) => {
                                const next = new Set(prev);
                                if (checked) {
                                  next.delete(socialKey);
                                } else {
                                  next.add(socialKey);
                                }
                                onSocialToggle?.(next);
                                return next;
                              });
                            }}
                            className="align-middle"
                          />
                        )}
                        <div
                          className="social-chip d-flex align-items-center border gap-1 px-2 py-1 rounded-pill pointer"
                          onClick={() => setSelectedSocialChip(social)}
                        >
                          <PublishedIcon
                            job_type={social?.job_type}
                            status={social?.status}
                            size={14}
                          />
                          <span
                            className="text-truncate"
                            style={{ maxWidth: "160px", fontSize: "12px" }}
                          >
                            {social?.channel_name || social?.job_type}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {(() => {
            const allMediaPlaylists =
              item?.playlists || (selectedMedia as any)?.playlists || [];
            if (
              !Array.isArray(allMediaPlaylists) ||
              allMediaPlaylists.length === 0
            ) {
              return null;
            }

            const allPlaylistsData =
              item?.playlistsData ||
              (selectedMedia as any)?.playlistsData ||
              [];
            const playlistNameById = new Map(
              (Array.isArray(allPlaylistsData) ? allPlaylistsData : []).map(
                (p: any) => [p?.id, p?.title]
              )
            );

            // Only show playlists the media being edited isn't already part
            // of - previewing an already-member playlist would just
            // duplicate an existing chip in the Playlist section.
            const existingPlaylistIds = new Set(
              ((currentMediaDetailsForEdit as any)?.playlists || []).map(
                (p: any) => p?.id
              )
            );
            const mediaPlaylists = allMediaPlaylists.filter(
              (playlist: any) => !existingPlaylistIds.has(playlist?.id)
            );

            if (mediaPlaylists.length === 0) {
              return null;
            }

            return (
              <div className="col-md-12 col-sm-12">
                <label className="mb-2">Playlists</label>
                <div className="d-flex flex-wrap gap-2">
                  {mediaPlaylists.map((playlist: any, idx: number) => {
                    const playlistKey = playlist?.id;
                    const isChecked = !excludedPlaylistKeys.has(playlistKey);
                    return (
                      <div
                        key={`${playlistKey}-${idx}`}
                        className="d-flex align-items-center gap-1"
                      >
                        {!readOnlyMode && (
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setExcludedPlaylistKeys((prev) => {
                                const next = new Set(prev);
                                if (checked) {
                                  next.delete(playlistKey);
                                } else {
                                  next.add(playlistKey);
                                }
                                onPlaylistToggle?.(next);
                                return next;
                              });
                            }}
                            className="align-middle"
                          />
                        )}
                        <div className="social-chip d-flex align-items-center border gap-1 px-2 py-1 rounded-pill">
                          <span
                            className="text-truncate"
                            style={{ maxWidth: "160px", fontSize: "12px" }}
                          >
                            {playlistNameById.get(playlistKey) ||
                              "Untitled playlist"}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {selectedSocialChip && (
            <Modal
              isOpen={!!selectedSocialChip}
              onClose={() => setSelectedSocialChip(null)}
              title="Social Preview"
              width="800px"
              closeOnOutsideClick={true}
            >
              <AllSocialPublish
                key={`${selectedSocialChip?.id || selectedSocialChip?.channel_name}-${selectedSocialChip?.job_type}`}
                isPreview={true}
                isWizard={false}
                isPreset={true}
                readOnlyMode={true}
                presetDetails={selectedSocialChipPresetDetails}
              />
            </Modal>
          )}

          {/* {!(isPreview && !(formik.values.live_channel.length > 0)) && (
            <div className="col-md-12 col-sm-12">
              <CustomSelect
                label="Live Channels"
                placeholder="24/7 Live Channels"
                value={processPermissionForSelect(
                  formik.values.live_channel || []
                )}
                handleChange={(newValue: any) => {
                  handlePermissionChange(
                    newValue.map((option: any) => option.value),
                    "live_channel"
                  );
                }}
                options={[
                  {
                    label: "test",
                    value: "test",
                  },
                ]}
                handleBlur={formik.handleBlur}
                multiSelect={true}
                touched={formik.touched.live_channel as boolean}
                hideArrow={true}
                updatedOptions={[
                  {
                    label: "test",
                    value: "test",
                  },
                ]}
                noOptionsMessage={() => "No destination available"}
                isNoOptionsMessage={true}
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.live_channel}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle("live_channel", checked);
                }}
                disabled={isPreview || !enabledFields.live_channel}
                readOnlyMode={readOnlyMode}
              />
            </div>
          )} */}

          {!(isPreview && !(formik.values.team.length > 0)) && (
            <div className="col-sm-12 col-md-4">
              <CustomSelect
                label="TEAM/PROPERTY"
                // disabled={!enabledFields.product}
                value={formik.values.team}
                options={bunchProductList
                  ?.filter(
                    (item: any) =>
                      item.access !== "none" && item.access !== "read"
                  )
                  ?.map((item: any) => ({
                    label: formatEntityName(item),
                    value: item?.id,
                  }))}
                showRequired={false}
                placeholder="Select Team"
                handleChange={(newValue: any) => {
                  formik.setFieldValue("team", newValue.value);
                }}
                updatedOptions={bunchProductList
                  ?.filter(
                    (item: any) =>
                      item.access !== "none" && item.access !== "read"
                  )
                  ?.map((item: any) => ({
                    label: formatEntityName(item),
                    value: item?.id,
                  }))}
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.team}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle("team", checked);
                }}
                disabled={isPreview || !enabledFields.team}
                showAsterisk={!!item?.meta_team_mode}
                asteriskClassName={getModeDotClass(item?.meta_team_mode)}
                disableLabelCheckbox={
                  isPreview && !(formik.values.team.length > 0)
                }
                readOnlyMode={readOnlyMode}
                hideArrow={isPreview ? true : false}
                customStyling={
                  isPreview ? { border: "none", marginLeft: "-10px" } : {}
                }
              />
            </div>
          )}

          {!(isPreview && !(formik.values.ctype.length > 0)) && (
            <div className="col-sm-12 col-md-4">
              <CustomSelect
                label="CONTENT TYPE"
                // disabled={!enabledFields.ctype}
                value={formik.values.ctype}
                options={content_type}
                showRequired={false}
                placeholder="Select Content Type"
                handleChange={(newValue: any) => {
                  formik.setFieldValue("ctype", newValue.value);
                }}
                updatedOptions={content_type}
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.ctype}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle("ctype", checked);
                }}
                disabled={isPreview || !enabledFields.ctype}
                showAsterisk={!!item?.content_type_mode}
                asteriskClassName={getModeDotClass(item?.content_type_mode)}
                disableLabelCheckbox={
                  isPreview && !(formik.values.ctype.length > 0)
                }
                readOnlyMode={readOnlyMode}
                hideArrow={isPreview ? true : false}
                customStyling={
                  isPreview ? { border: "none", marginLeft: "-10px" } : {}
                }
              />
            </div>
          )}

          {!(isPreview && !(formik.values.production_type.length > 0)) && (
            <div className="col-sm-12 col-md-4">
              <CustomSelect
                label="Production Type"
                // disabled={!enabledFields.production_type}
                value={formik.values.production_type}
                options={production_type}
                showRequired={false}
                placeholder="Select Production Type"
                handleChange={(newValue: any) => {
                  formik.setFieldValue("production_type", newValue.value);
                }}
                updatedOptions={production_type}
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.production_type}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle("production_type", checked);
                }}
                disabled={isPreview || !enabledFields.production_type}
                showAsterisk={
                  !!selectedMedia?.production_type_mode ||
                  !!item?.production_type_mode
                }
                asteriskClassName={getModeDotClass(
                  selectedMedia?.production_type_mode ||
                    item?.production_type_mode ||
                    ""
                )}
                disableLabelCheckbox={
                  isPreview && !(formik.values.production_type.length > 0)
                }
                readOnlyMode={readOnlyMode}
                hideArrow={isPreview ? true : false}
                customStyling={
                  isPreview ? { border: "none", marginLeft: "-10px" } : {}
                }
              />
            </div>
          )}

          {!(isPreview && !(formik.values.lang.length > 0)) && (
            <div className="col-sm-12 col-md-4">
              <CustomSelect
                label="LANGUAGE"
                // disabled={!enabledFields.lang}
                value={formik.values.lang}
                options={languageOptions}
                showRequired={false}
                placeholder="Select Language"
                handleChange={(newValue: any) => {
                  formik.setFieldValue("lang", newValue.value);
                }}
                updatedOptions={languageOptions}
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.lang}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle("lang", checked);
                }}
                disabled={isPreview || !enabledFields.lang}
                showAsterisk={
                  !!selectedMedia?.language_mode || !!item?.language_mode
                }
                asteriskClassName={getModeDotClass(
                  selectedMedia?.language_mode || item?.language_mode || ""
                )}
                disableLabelCheckbox={
                  isPreview && !(formik.values.lang.length > 0)
                }
                readOnlyMode={readOnlyMode}
                hideArrow={isPreview ? true : false}
                customStyling={
                  isPreview ? { border: "none", marginLeft: "-10px" } : {}
                }
              />
            </div>
          )}

          {!(isPreview && !(formik.values.category.length > 0)) && (
            <div className="col-sm-12 col-md-4">
              <CustomSelect
                label="CATEGORY"
                // disabled={!enabledFields.category}
                value={formik.values.category}
                options={categoryOptions}
                showRequired={false}
                placeholder="Select Category"
                handleChange={(newValue: any) => {
                  formik.setFieldValue("category", newValue.value);
                  formik.setFieldValue("subcategory", "");
                }}
                updatedOptions={categoryOptions}
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.category}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle("category", checked);
                }}
                disabled={isPreview || !enabledFields.category}
                showAsterisk={
                  !!selectedMedia?.category_mode || !!item?.category_mode
                }
                asteriskClassName={getModeDotClass(
                  selectedMedia?.category_mode || item?.category_mode || ""
                )}
                disableLabelCheckbox={
                  isPreview && !(formik.values.category.length > 0)
                }
                readOnlyMode={readOnlyMode}
                hideArrow={isPreview ? true : false}
                customStyling={
                  isPreview ? { border: "none", marginLeft: "-10px" } : {}
                }
              />
            </div>
          )}

          {!(isPreview && !(formik.values.subcategory.length > 0)) && (
            <div className="col-sm-12 col-md-4">
              <CustomSelect
                label="SUBCATEGORY"
                // disabled={!enabledFields.subcategory}
                value={formik.values.subcategory}
                placeholder={
                  formik.values.category
                    ? "Select Sub Category"
                    : "Select Category First"
                }
                showRequired={false}
                handleChange={(newValue: any) => {
                  formik.setFieldValue("subcategory", newValue.value);
                }}
                // options={bunchCategoryList[formik.values.category]}
                options={subCategoryList?.[formik.values.category]?.map(
                  (item: any) => ({
                    label: item?.name,
                    value: item?.name,
                  })
                )}
                updatedOptions={subCategoryList?.[formik.values.category]?.map(
                  (item: any) => ({
                    label: item?.name,
                    value: item?.name,
                  })
                )}
                error={formik.errors.subcategory}
                touched={formik.touched.subcategory as boolean}
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.subcategory}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle("subcategory", checked);
                }}
                disabled={isPreview || !enabledFields.subcategory}
                showAsterisk={
                  !!selectedMedia?.sub_category_mode ||
                  !!item?.sub_category_mode
                }
                asteriskClassName={getModeDotClass(
                  selectedMedia?.sub_category_mode ||
                    item?.sub_category_mode ||
                    ""
                )}
                disableLabelCheckbox={
                  isPreview && !(formik.values.subcategory.length > 0)
                }
                readOnlyMode={readOnlyMode}
                hideArrow={isPreview ? true : false}
                customStyling={
                  isPreview ? { border: "none", marginLeft: "-10px" } : {}
                }
              />
            </div>
          )}

          {!(isPreview && !(formik.values.agency.length > 0)) && (
            <div className="col-sm-12 col-md-4">
              <CustomSelect
                label="AGENCY"
                value={String(formik.values.agency)}
                // disabled={!enabledFields.agency}
                handleChange={(newValue: any) => {
                  formik.setFieldValue("agency", newValue.value);
                }}
                options={autoSelectAgency?.map((item: any) => ({
                  label: formatEntityName(item),
                  value: item?.id,
                }))}
                handleBlur={formik.handleBlur}
                showRequired={false}
                error={formik.errors.agency}
                touched={formik.touched.agency as boolean}
                updatedOptions={autoSelectAgency?.map((item: any) => ({
                  label: formatEntityName(item),
                  value: item?.id,
                }))}
                labelCheckbox={true}
                onInputChangeCallback={(params) => {
                  if (params.keyword.length >= 3) {
                    getListAgencies?.({
                      name: params.keyword,
                      setInRedux: false,
                    }).then((res: any) => {
                      setAutoSelectAgency(res || []);
                    });
                  } else {
                    getListAgencies?.({ name: "", setInRedux: false }).then(
                      (res: any) => {
                        setAutoSelectAgency(res || []);
                      }
                    );
                  }
                }}
                labelCheckboxChecked={enabledFields.agency}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle("agency", checked);
                }}
                disabled={isPreview || !enabledFields.agency}
                showAsterisk={
                  !!selectedMedia?.agency_mode || !!item?.agency_mode
                }
                asteriskClassName={getModeDotClass(
                  selectedMedia?.agency_mode || item?.agency_mode || ""
                )}
                disableLabelCheckbox={
                  isPreview && !(formik.values.agency.length > 0)
                }
                readOnlyMode={readOnlyMode}
                hideArrow={true}
                loader={true}
                customStyling={
                  isPreview ? { border: "none", marginLeft: "-10px" } : {}
                }
                creditData={selectedAgency}
              />
            </div>
          )}

          {!(isPreview && !(formik.values.agency_credits.length > 0)) && (
            <div className="col-sm-12 col-md-4">
              <CustomSelect
                label="Agency Credits"
                value={processPermissionForSelect(
                  formik?.values?.agency_credits || []
                )}
                // disabled={!enabledFields.agency_credits}
                handleChange={(newValue: any) => {
                  handlePermissionChange(
                    newValue.map((option: any) => option.value),
                    "agency_credits"
                  );
                }}
                options={autoSelectAgencyCredit?.map((item: any) => ({
                  label: formatEntityName(item),
                  value: item?.id,
                }))}
                handleBlur={formik.handleBlur}
                multiSelect={true}
                hideArrow={true}
                loader={true}
                showRequired={false}
                error={formik.errors.agency_credits}
                touched={formik.touched.agency_credits as boolean}
                updatedOptions={autoSelectAgencyCredit?.map((item: any) => ({
                  label: formatEntityName(item),
                  value: item?.id,
                }))}
                onInputChangeCallback={(params) => {
                  if (params.keyword.length >= 3) {
                    getListAgencies?.({
                      name: params.keyword,
                      setInRedux: false,
                    }).then((res: any) => {
                      setAutoSelectAgencyCredit(res || []);
                    });
                  } else {
                    getListAgencies?.({ name: "", setInRedux: false }).then(
                      (res: any) => {
                        setAutoSelectAgencyCredit(res || []);
                      }
                    );
                  }
                }}
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.agency_credits}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle("agency_credits", checked);
                }}
                disabled={isPreview || !enabledFields.agency_credits}
                disableLabelCheckbox={
                  isPreview && !(formik.values.agency_credits.length > 0)
                }
                readOnlyMode={readOnlyMode}
                showAsterisk={
                  !!selectedMedia?.agency_credits_mode ||
                  !!item?.agency_credits_mode
                }
                asteriskClassName={getModeDotClass(
                  selectedMedia?.agency_credits_mode ||
                    item?.agency_credits_mode ||
                    ""
                )}
                customStyling={
                  isPreview ? { border: "none", marginLeft: "-10px" } : {}
                }
                creditData={selectedAgencyCredit}
                // showAsterisk={currentMediaDetailsForEdit?.agency_credits?.length > 0 && enabledFields.agency_credits}
              />
            </div>
          )}

          {/* {!(isPreview && !(formik.values.genre.length > 0)) && (
            <div className="col-sm-12 col-md-4">
              <CustomSelect
                value={formik.values.genre || ''}
                label="Genre"
                options={genre.map((item: any) => ({ label: item.name, value: item.name }))}
                // disabled={!enabledFields.genre}
                showRequired={false}
                placeholder="Select Genre"
                handleChange={(newValue: any) => {
                  formik.setFieldValue('subgenre', '');
                  formik.setFieldValue('genre', newValue.value);
                }}
                hideArrow={false}
                updatedOptions={genre.map((item: any) => ({ label: item.name, value: item.id }))}
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.genre}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle('genre', checked);
                }}
                disabled={isPreview || !enabledFields.genre}
                showAsterisk={currentMediaDetailsForEdit?.genre?.length > 0 && enabledFields.genre}
                disableLabelCheckbox={isPreview && !(formik.values.genre.length > 0)}
                readOnlyMode={readOnlyMode}
                customStyling={isPreview ? { border: 'none', marginLeft: '-10px' } : {}}
              />
            </div>
          )} */}

          {/* {!(isPreview && !(formik.values.subgenre.length > 0)) && (
            <div className="col-sm-12 col-md-4">
              <CustomSelect
                value={formik.values.subgenre || ''}
                label="Sub Genre"
                options={subgenres[formik.values.genre || ''] || []}
                // disabled={!enabledFields.subgenre}
                showRequired={false}
                placeholder={formik.values.genre ? 'select sub genre' : 'select genre first'}
                handleChange={(newValue: any) => {
                  formik.setFieldValue('subgenre', newValue.value);
                }}
                hideArrow={false}
                updatedOptions={subgenres[formik.values.genre || ''] || []}
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.subgenre}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle('subgenre', checked);
                }}
                disabled={isPreview || !enabledFields.subgenre}
                showAsterisk={currentMediaDetailsForEdit?.subgenre?.length > 0 && enabledFields.subgenre}
                disableLabelCheckbox={isPreview && !(formik.values.subgenre.length > 0)}
                readOnlyMode={readOnlyMode}
                customStyling={isPreview ? { border: 'none', marginLeft: '-10px' } : {}}
              />
            </div>
          )} */}
          {!(isPreview && !((formik?.values?.mfk || 0) > 0)) && (
            <div className="col-sm-12 col-md-4">
              <CustomSelect
                value={formik?.values?.mfk || ""}
                label="Content Rating"
                options={[
                  { label: "Made for Kids", value: 1 },
                  { label: "Made for General Audience", value: 2 },
                  { label: "Made for 18+", value: 3 },
                ]}
                // disabled={!enabledFields.mfk}
                showRequired={false}
                placeholder="Select Content Rating"
                handleChange={(newValue: any) => {
                  formik.setFieldValue("mfk", parseInt(newValue.value));
                }}
                // hideArrow={false}
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.mfk}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle("mfk", checked);
                }}
                disabled={isPreview || !enabledFields.mfk}
                showAsterisk={
                  !!selectedMedia?.content_rating_mode ||
                  !!item?.content_rating_mode
                }
                asteriskClassName={getModeDotClass(
                  selectedMedia?.content_rating_mode ||
                    item?.content_rating_mode ||
                    ""
                )}
                disableLabelCheckbox={isPreview && !(formik.values.mfk > 0)}
                readOnlyMode={readOnlyMode}
                hideArrow={isPreview ? true : false}
                customStyling={
                  isPreview ? { border: "none", marginLeft: "-10px" } : {}
                }
              />
            </div>
          )}
        </div>
        <div className="row gy-3 m-0">
          {shouldShowContributors() && (
            <h6 className="mt-4 mb-2">Contributors</h6>
          )}
          {!(isPreview && !(formik.values.editor.length > 0)) && (
            <div className="col-sm-12 col-md-4">
              <CustomSelect
                label="Editor"
                value={processPermissionForSelect(formik.values.editor)}
                // disabled={!enabledFields.editor}
                handleChange={(newValue: any) => {
                  handlePermissionChange(
                    newValue.map((option: any) => option.value),
                    "editor"
                  );
                  getListUser?.({ name: "" });
                }}
                onInputChangeCallback={(params) => {
                  if (params.keyword.length >= 3) {
                    getListUser?.({ name: params.keyword });
                  } else {
                    getListUser?.({ name: "" });
                  }
                }}
                options={mfuEditor}
                error={formik.errors.editor}
                handleBlur={formik.handleBlur}
                multiSelect={true}
                touched={formik.touched.editor as boolean}
                hideArrow={true}
                updatedOptions={userList?.map((val: any) => {
                  return {
                    label: ` ${val?.name}  ${
                      val?.email ? "(" + truncateText(val?.email, 20) + ")" : ""
                    } `,
                    value: val?.id,
                  };
                })}
                loader={true}
                showRequired={false}
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.editor}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle("editor", checked);
                }}
                disabled={isPreview || !enabledFields.editor}
                disableLabelCheckbox={
                  isPreview && !(formik.values.editor.length > 0)
                }
                showAsterisk={
                  !!selectedMedia?.contributors?.editors_mode ||
                  !!item?.contributors?.editors_mode
                }
                asteriskClassName={getModeDotClass(
                  selectedMedia?.contributors?.editors_mode ||
                    item?.contributors?.editors_mode ||
                    ""
                )}
                readOnlyMode={readOnlyMode}
                customStyling={
                  isPreview ? { border: "none", marginLeft: "-10px" } : {}
                }
                creditData={selectedContributor}
              />
            </div>
          )}
          {!(isPreview && !(formik.values.anchor.length > 0)) && (
            <div className="col-sm-12 col-md-4">
              <CustomSelect
                label="Anchor"
                value={processPermissionForSelect(formik.values.anchor)}
                handleChange={(newValue: any) => {
                  handlePermissionChange(
                    newValue.map((option: any) => option.value),
                    "anchor"
                  );
                  getListUser?.({ name: "" });
                }}
                onInputChangeCallback={(params) => {
                  if (params.keyword.length >= 3) {
                    getListUser?.({ name: params.keyword });
                  } else {
                    getListUser?.({ name: "" });
                  }
                }}
                options={mfuAnchor}
                handleBlur={formik.handleBlur}
                multiSelect={true}
                touched={formik.touched.anchor as boolean}
                // disabled={!enabledFields.anchor}
                hideArrow={true}
                updatedOptions={userList?.map((val: any) => {
                  return {
                    label: ` ${val?.name}  ${
                      val?.email ? "(" + truncateText(val?.email, 20) + ")" : ""
                    } `,
                    value: val?.id,
                  };
                })}
                loader={true}
                showRequired={false}
                error={formik.errors.anchor}
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.anchor}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle("anchor", checked);
                }}
                disabled={isPreview || !enabledFields.anchor}
                disableLabelCheckbox={
                  isPreview && !(formik.values.anchor.length > 0)
                }
                showAsterisk={
                  !!selectedMedia?.contributors?.anchors_mode ||
                  !!item?.contributors?.anchors_mode
                }
                asteriskClassName={getModeDotClass(
                  selectedMedia?.contributors?.anchors_mode ||
                    item?.contributors?.anchors_mode ||
                    ""
                )}
                readOnlyMode={readOnlyMode}
                customStyling={
                  isPreview ? { border: "none", marginLeft: "-10px" } : {}
                }
                creditData={selectedContributor}
              />
            </div>
          )}
          {!(isPreview && !(formik.values.script_writer.length > 0)) && (
            <div className="col-sm-12 col-md-4">
              <CustomSelect
                label="Script Writer"
                value={processPermissionForSelect(formik.values.script_writer)}
                handleChange={(newValue: any) => {
                  handlePermissionChange(
                    newValue.map((option: any) => option.value),
                    "script_writer"
                  );
                  getListUser?.({ name: "" });
                }}
                onInputChangeCallback={(params) => {
                  if (params.keyword.length >= 3) {
                    getListUser?.({ name: params.keyword });
                  } else {
                    getListUser?.({ name: "" });
                  }
                }}
                options={mfuScriptWriter}
                handleBlur={formik.handleBlur}
                multiSelect={true}
                touched={formik.touched.script_writer as boolean}
                hideArrow={true}
                // disabled={!enabledFields.script_writer}
                updatedOptions={userList?.map((val: any) => {
                  return {
                    label: ` ${val?.name}  ${
                      val?.email ? "(" + truncateText(val?.email, 20) + ")" : ""
                    } `,
                    value: val?.id,
                  };
                })}
                loader={true}
                showRequired={false}
                error={formik.errors.script_writer}
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.script_writer}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle("script_writer", checked);
                }}
                disabled={isPreview || !enabledFields.script_writer}
                disableLabelCheckbox={
                  isPreview && !(formik.values.script_writer.length > 0)
                }
                // showAsterisk={currentMediaDetailsForEdit?.script_writer?.length > 0 && enabledFields.script_writer}
                readOnlyMode={readOnlyMode}
                showAsterisk={
                  !!selectedMedia?.contributors?.script_writers_mode ||
                  !!item?.contributors?.script_writers_mode
                }
                asteriskClassName={getModeDotClass(
                  selectedMedia?.contributors?.script_writers_mode ||
                    item?.contributors?.script_writers_mode ||
                    ""
                )}
                customStyling={
                  isPreview ? { border: "none", marginLeft: "10px" } : {}
                }
                creditData={selectedContributor}
              />
            </div>
          )}
          {!(isPreview && !(formik.values.producer.length > 0)) && (
            <div className="col-sm-12 col-md-4">
              <CustomSelect
                label="Producer"
                value={processPermissionForSelect(formik.values.producer)}
                handleChange={(newValue: any) => {
                  handlePermissionChange(
                    newValue.map((option: any) => option.value),
                    "producer"
                  );
                  getListUser?.({ name: "" });
                }}
                onInputChangeCallback={(params) => {
                  if (params.keyword.length >= 3) {
                    getListUser?.({ name: params.keyword });
                  } else {
                    getListUser?.({ name: "" });
                  }
                }}
                options={mfuProducer}
                handleBlur={formik.handleBlur}
                multiSelect={true}
                touched={formik.touched.producer as boolean}
                hideArrow={true}
                // disabled={!enabledFields.producer}
                updatedOptions={userList?.map((val: any) => {
                  return {
                    label: ` ${val?.name}  ${
                      val?.email ? "(" + truncateText(val?.email, 20) + ")" : ""
                    } `,
                    value: val?.id,
                  };
                })}
                loader={true}
                showRequired={false}
                error={formik.errors.producer}
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.producer}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle("producer", checked);
                }}
                disabled={isPreview || !enabledFields.producer}
                disableLabelCheckbox={
                  isPreview && !(formik.values.producer.length > 0)
                }
                showAsterisk={
                  !!selectedMedia?.contributors?.producers_mode ||
                  !!item?.contributors?.producers_mode
                }
                asteriskClassName={getModeDotClass(
                  selectedMedia?.contributors?.producers_mode ||
                    item?.contributors?.producers_mode ||
                    ""
                )}
                readOnlyMode={readOnlyMode}
                customStyling={
                  isPreview ? { border: "none", marginLeft: "-10px" } : {}
                }
                creditData={selectedContributor}
              />
            </div>
          )}
          {!(isPreview && !(formik.values.cameraman.length > 0)) && (
            <div className="col-sm-12 col-md-4">
              <CustomSelect
                label="Cameraman"
                value={processPermissionForSelect(formik.values.cameraman)}
                handleChange={(newValue: any) => {
                  handlePermissionChange(
                    newValue.map((option: any) => option.value),
                    "cameraman"
                  );
                  getListUser?.({ name: "" });
                }}
                onInputChangeCallback={(params) => {
                  if (params.keyword.length >= 3) {
                    getListUser?.({ name: params.keyword });
                  } else {
                    getListUser?.({ name: "" });
                  }
                }}
                // error={formik.errors.producer}
                options={mfuCameraman}
                handleBlur={formik.handleBlur}
                multiSelect={true}
                touched={formik.touched.cameraman as boolean}
                hideArrow={true}
                // disabled={!enabledFields.cameraman}
                updatedOptions={userList?.map((val: any) => {
                  return {
                    label: ` ${val?.name}  ${
                      val?.email ? "(" + truncateText(val?.email, 20) + ")" : ""
                    } `,
                    value: val?.id,
                  };
                })}
                loader={true}
                showRequired={false}
                error={formik.errors.cameraman}
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.cameraman}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle("cameraman", checked);
                }}
                disabled={isPreview || !enabledFields.cameraman}
                disableLabelCheckbox={
                  isPreview && !(formik.values.cameraman.length > 0)
                }
                showAsterisk={
                  !!selectedMedia?.contributors?.cameramen_mode ||
                  !!item?.contributors?.cameramen_mode
                }
                asteriskClassName={getModeDotClass(
                  selectedMedia?.contributors?.cameramen_mode ||
                    item?.contributors?.cameramen_mode ||
                    ""
                )}
                readOnlyMode={readOnlyMode}
                customStyling={
                  isPreview ? { border: "none", marginLeft: "-10px" } : {}
                }
                creditData={selectedContributor}
              />
            </div>
          )}
          {!(isPreview && !(formik.values.photographer.length > 0)) && (
            <div className="col-sm-12 col-md-4">
              <CustomSelect
                label="Photographer"
                value={processPermissionForSelect(formik.values.photographer)}
                // disabled={!enabledFields.editor}
                handleChange={(newValue: any) => {
                  handlePermissionChange(
                    newValue.map((option: any) => option.value),
                    "photographer"
                  );
                  getListUser?.({ name: "" });
                }}
                onInputChangeCallback={(params) => {
                  if (params.keyword.length >= 3) {
                    getListUser?.({ name: params.keyword });
                  } else {
                    getListUser?.({ name: "" });
                  }
                }}
                options={mfuEditor}
                error={formik.errors.photographer}
                handleBlur={formik.handleBlur}
                multiSelect={true}
                touched={formik.touched.photographer as boolean}
                hideArrow={true}
                updatedOptions={userList?.map((val: any) => {
                  return {
                    label: ` ${val?.name}  ${
                      val?.email ? "(" + truncateText(val?.email, 20) + ")" : ""
                    } `,
                    value: val?.id,
                  };
                })}
                loader={true}
                showRequired={false}
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.photographer}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle("photographer", checked);
                }}
                disabled={isPreview || !enabledFields.photographer}
                disableLabelCheckbox={
                  isPreview && !(formik.values.photographer.length > 0)
                }
                showAsterisk={
                  !!selectedMedia?.contributors?.photographers_mode ||
                  !!item?.contributors?.photographers_mode
                }
                asteriskClassName={getModeDotClass(
                  selectedMedia?.contributors?.photographers_mode ||
                    item?.contributors?.photographers_mode ||
                    ""
                )}
                readOnlyMode={readOnlyMode}
                customStyling={
                  isPreview ? { border: "none", marginLeft: "-10px" } : {}
                }
                creditData={selectedContributor}
              />
            </div>
          )}
          {!(isPreview && !(formik.values.researcher.length > 0)) && (
            <div className="col-sm-12 col-md-4">
              <CustomSelect
                label="Researcher"
                value={processPermissionForSelect(formik.values.researcher)}
                // disabled={!enabledFields.editor}
                handleChange={(newValue: any) => {
                  handlePermissionChange(
                    newValue.map((option: any) => option.value),
                    "researcher"
                  );
                  getListUser?.({ name: "" });
                }}
                onInputChangeCallback={(params) => {
                  if (params.keyword.length >= 3) {
                    getListUser?.({ name: params.keyword });
                  } else {
                    getListUser?.({ name: "" });
                  }
                }}
                options={mfuEditor}
                error={formik.errors.researcher}
                handleBlur={formik.handleBlur}
                multiSelect={true}
                touched={formik.touched.researcher as boolean}
                hideArrow={true}
                updatedOptions={userList?.map((val: any) => {
                  return {
                    label: ` ${val?.name}  ${
                      val?.email ? "(" + truncateText(val?.email, 20) + ")" : ""
                    } `,
                    value: val?.id,
                  };
                })}
                loader={true}
                showRequired={false}
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.researcher}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle("researcher", checked);
                }}
                disabled={isPreview || !enabledFields.researcher}
                disableLabelCheckbox={
                  isPreview && !(formik.values.researcher.length > 0)
                }
                showAsterisk={
                  !!selectedMedia?.contributors?.researchers_mode ||
                  !!item?.contributors?.researchers_mode
                }
                asteriskClassName={getModeDotClass(
                  selectedMedia?.contributors?.researchers_mode ||
                    item?.contributors?.researchers_mode ||
                    ""
                )}
                readOnlyMode={readOnlyMode}
                customStyling={
                  isPreview ? { border: "none", marginLeft: "-10px" } : {}
                }
                creditData={selectedContributor}
              />
            </div>
          )}
          {!(isPreview && !(formik.values.compiler.length > 0)) && (
            <div className="col-sm-12 col-md-4">
              <CustomSelect
                label="Compiler"
                value={processPermissionForSelect(formik.values.compiler)}
                // disabled={!enabledFields.editor}
                handleChange={(newValue: any) => {
                  handlePermissionChange(
                    newValue.map((option: any) => option.value),
                    "compiler"
                  );
                  getListUser?.({ name: "" });
                }}
                onInputChangeCallback={(params) => {
                  if (params.keyword.length >= 3) {
                    getListUser?.({ name: params.keyword });
                  } else {
                    getListUser?.({ name: "" });
                  }
                }}
                options={mfuEditor}
                error={formik.errors.compiler}
                handleBlur={formik.handleBlur}
                multiSelect={true}
                touched={formik.touched.compiler as boolean}
                hideArrow={true}
                updatedOptions={userList?.map((val: any) => {
                  return {
                    label: ` ${val?.name}  ${
                      val?.email ? "(" + truncateText(val?.email, 20) + ")" : ""
                    } `,
                    value: val?.id,
                  };
                })}
                loader={true}
                showRequired={false}
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.compiler}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle("compiler", checked);
                }}
                disabled={isPreview || !enabledFields.compiler}
                disableLabelCheckbox={
                  isPreview && !(formik.values.compiler.length > 0)
                }
                showAsterisk={
                  !!selectedMedia?.contributors?.compilers_mode ||
                  !!item?.contributors?.compilers_mode
                }
                asteriskClassName={getModeDotClass(
                  selectedMedia?.contributors?.compilers_mode ||
                    item?.contributors?.compilers_mode ||
                    ""
                )}
                readOnlyMode={readOnlyMode}
                customStyling={
                  isPreview ? { border: "none", marginLeft: "-10px" } : {}
                }
                creditData={selectedContributor}
              />
            </div>
          )}
          {!(isPreview && !(formik.values.curator.length > 0)) && (
            <div className="col-sm-12 col-md-4">
              <CustomSelect
                label="Curator"
                value={processPermissionForSelect(formik.values.curator)}
                // disabled={!enabledFields.editor}
                handleChange={(newValue: any) => {
                  handlePermissionChange(
                    newValue.map((option: any) => option.value),
                    "curator"
                  );
                  getListUser?.({ name: "" });
                }}
                onInputChangeCallback={(params) => {
                  if (params.keyword.length >= 3) {
                    getListUser?.({ name: params.keyword });
                  } else {
                    getListUser?.({ name: "" });
                  }
                }}
                options={mfuEditor}
                error={formik.errors.curator}
                handleBlur={formik.handleBlur}
                multiSelect={true}
                touched={formik.touched.curator as boolean}
                hideArrow={true}
                updatedOptions={userList?.map((val: any) => {
                  return {
                    label: ` ${val?.name}  ${
                      val?.email ? "(" + truncateText(val?.email, 20) + ")" : ""
                    } `,
                    value: val?.id,
                  };
                })}
                loader={true}
                showRequired={false}
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.curator}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle("curator", checked);
                }}
                disabled={isPreview || !enabledFields.curator}
                disableLabelCheckbox={
                  isPreview && !(formik.values.curator.length > 0)
                }
                showAsterisk={
                  !!selectedMedia?.contributors?.curators_mode ||
                  !!item?.contributors?.curators_mode
                }
                asteriskClassName={getModeDotClass(
                  selectedMedia?.contributors?.curators_mode ||
                    item?.contributors?.curators_mode ||
                    ""
                )}
                readOnlyMode={readOnlyMode}
                customStyling={
                  isPreview ? { border: "none", marginLeft: "-10px" } : {}
                }
                creditData={selectedContributor}
              />
            </div>
          )}
          {!(isPreview && !(formik.values.reporter.length > 0)) && (
            <div className="col-sm-12 col-md-4">
              <CustomSelect
                label="Reporter"
                value={processPermissionForSelect(formik.values.reporter)}
                // disabled={!enabledFields.editor}
                handleChange={(newValue: any) => {
                  handlePermissionChange(
                    newValue.map((option: any) => option.value),
                    "reporter"
                  );
                  getListUser?.({ name: "" });
                }}
                onInputChangeCallback={(params) => {
                  if (params.keyword.length >= 3) {
                    getListUser?.({ name: params.keyword });
                  } else {
                    getListUser?.({ name: "" });
                  }
                }}
                options={mfuEditor}
                error={formik.errors.reporter}
                handleBlur={formik.handleBlur}
                multiSelect={true}
                touched={formik.touched.reporter as boolean}
                hideArrow={true}
                updatedOptions={userList?.map((val: any) => {
                  return {
                    label: ` ${val?.name}  ${
                      val?.email ? "(" + truncateText(val?.email, 20) + ")" : ""
                    } `,
                    value: val?.id,
                  };
                })}
                loader={true}
                showRequired={false}
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.reporter}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle("reporter", checked);
                }}
                disabled={isPreview || !enabledFields.reporter}
                disableLabelCheckbox={
                  isPreview && !(formik.values.reporter.length > 0)
                }
                showAsterisk={
                  !!selectedMedia?.contributors?.reporters_mode ||
                  !!item?.contributors?.reporters_mode
                }
                asteriskClassName={getModeDotClass(
                  selectedMedia?.contributors?.reporters_mode ||
                    item?.contributors?.reporters_mode ||
                    ""
                )}
                readOnlyMode={readOnlyMode}
                customStyling={
                  isPreview ? { border: "none", marginLeft: "-10px" } : {}
                }
                creditData={selectedContributor}
              />
            </div>
          )}
          {!(isPreview && !(formik.values.author.length > 0)) && (
            <div className="col-sm-12 col-md-4">
              <CustomSelect
                label="Author"
                value={processPermissionForSelect(formik.values.author)}
                // disabled={!enabledFields.editor}
                handleChange={(newValue: any) => {
                  handlePermissionChange(
                    newValue.map((option: any) => option.value),
                    "author"
                  );
                  getListUser?.({ name: "" });
                }}
                onInputChangeCallback={(params) => {
                  if (params.keyword.length >= 3) {
                    getListUser?.({ name: params.keyword });
                  } else {
                    getListUser?.({ name: "" });
                  }
                }}
                options={mfuEditor}
                error={formik.errors.author}
                handleBlur={formik.handleBlur}
                multiSelect={true}
                touched={formik.touched.author as boolean}
                hideArrow={true}
                updatedOptions={userList?.map((val: any) => {
                  return {
                    label: ` ${val?.name}  ${
                      val?.email ? "(" + truncateText(val?.email, 20) + ")" : ""
                    } `,
                    value: val?.id,
                  };
                })}
                loader={true}
                showRequired={false}
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.author}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle("author", checked);
                }}
                disabled={isPreview || !enabledFields.author}
                disableLabelCheckbox={
                  isPreview && !(formik.values.author.length > 0)
                }
                showAsterisk={
                  !!selectedMedia?.contributors?.authors_mode ||
                  !!item?.contributors?.authors_mode
                }
                asteriskClassName={getModeDotClass(
                  selectedMedia?.contributors?.authors_mode ||
                    item?.contributors?.authors_mode ||
                    ""
                )}
                readOnlyMode={readOnlyMode}
                customStyling={
                  isPreview ? { border: "none", marginLeft: "-10px" } : {}
                }
                creditData={selectedContributor}
              />
            </div>
          )}
        </div>

        <div className="row gy-3 m-0">
          {shouldShowCredit() && <h6 className="mt-4 mb-2">Credits</h6>}
          {!(isPreview && !(formik.values.imagecredit.length > 0)) && (
            <div className="col-sm-12 col-md-4">
              <CustomSelect
                label="Image Credit"
                value={processPermissionForSelect(formik.values.imagecredit)}
                handleChange={(newValue: any) => {
                  handlePermissionChange(
                    newValue.map((option: any) => option.value),
                    "imagecredit"
                  );
                  store.dispatch(getCreditList({ name: "", key: "image" }));
                }}
                onInputChangeCallback={(params) => {
                  if (params.keyword.length >= 3) {
                    store.dispatch(
                      getCreditList({ name: params.keyword, key: "image" })
                    );
                  } else {
                    store.dispatch(getCreditList({ name: "", key: "image" }));
                  }
                }}
                // error={formik.errors.producer}
                options={imageCreditList?.list?.map((val: any) => {
                  return {
                    label: ` ${val?.name}  ${
                      val?.email ? "(" + truncateText(val?.email, 20) + ")" : ""
                    } `,
                    value: val?.id,
                  };
                })}
                handleBlur={formik.handleBlur}
                multiSelect={true}
                touched={formik.touched.imagecredit as boolean}
                hideArrow={true}
                updatedOptions={imageCreditList?.list?.map((val: any) => {
                  return {
                    label: ` ${val?.name}  ${
                      val?.email ? "(" + truncateText(val?.email, 20) + ")" : ""
                    } `,
                    value: val?.id,
                  };
                })}
                // disabled={!enabledFields.imagecredit}
                creditData={selectedCredits}
                checkBox={false}
                loader={true}
                showRequired={false}
                showAsterisk={
                  !!selectedMedia?.credits?.image_credits_mode ||
                  !!item?.credits?.image_credits_mode
                }
                asteriskClassName={getModeDotClass(
                  selectedMedia?.credits?.image_credits_mode ||
                    item?.credits?.image_credits_mode ||
                    ""
                )}
                error={formik.errors.imagecredit}
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.imagecredit}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle("imagecredit", checked);
                }}
                disabled={isPreview || !enabledFields.imagecredit}
                disableLabelCheckbox={
                  isPreview && !(formik.values.imagecredit.length > 0)
                }
                // showAsterisk={currentMediaDetailsForEdit?.imagecredit?.length > 0 && enabledFields.imagecredit}
                readOnlyMode={readOnlyMode}
                customStyling={
                  isPreview ? { border: "none", marginLeft: "-10px" } : {}
                }
              />
            </div>
          )}
          {!(isPreview && !(formik.values.videocredit.length > 0)) && (
            <div className="col-sm-12 col-md-4">
              <CustomSelect
                label="Video Credit"
                value={processPermissionForSelect(formik.values.videocredit)}
                handleChange={(newValue: any) => {
                  handlePermissionChange(
                    newValue.map((option: any) => option.value),
                    "videocredit"
                  );
                  store.dispatch(getCreditList({ name: "", key: "video" }));
                }}
                onInputChangeCallback={(params) => {
                  if (params.keyword.length >= 3) {
                    store.dispatch(
                      getCreditList({ name: params.keyword, key: "video" })
                    );
                  } else {
                    store.dispatch(getCreditList({ name: "", key: "video" }));
                  }
                }}
                options={videoCreditList?.list?.map((val: any) => {
                  return {
                    label: ` ${val?.name}  ${
                      val?.email ? "(" + truncateText(val?.email, 20) + ")" : ""
                    } `,
                    value: val?.id,
                  };
                })}
                handleBlur={formik.handleBlur}
                // disabled={!enabledFields.videocredit}
                multiSelect={true}
                touched={formik.touched.videocredit as boolean}
                hideArrow={true}
                updatedOptions={videoCreditList?.list?.map((val: any) => {
                  return {
                    label: ` ${val?.name}  ${
                      val?.email ? "(" + truncateText(val?.email, 20) + ")" : ""
                    } `,
                    value: val?.id,
                  };
                })}
                creditData={selectedCredits}
                checkBox={false}
                loader={true}
                showRequired={false}
                showAsterisk={
                  !!selectedMedia?.credits?.video_credits_mode ||
                  !!item?.credits?.video_credits_mode
                }
                asteriskClassName={getModeDotClass(
                  selectedMedia?.credits?.video_credits_mode ||
                    item?.credits?.video_credits_mode ||
                    ""
                )}
                error={formik.errors.videocredit}
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.videocredit}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle("videocredit", checked);
                }}
                disabled={isPreview || !enabledFields.videocredit}
                disableLabelCheckbox={
                  isPreview && !(formik.values.videocredit.length > 0)
                }
                // showAsterisk={currentMediaDetailsForEdit?.videocredit?.length > 0 && enabledFields.videocredit}
                readOnlyMode={readOnlyMode}
                customStyling={
                  isPreview ? { border: "none", marginLeft: "-10px" } : {}
                }
              />
            </div>
          )}
          {!(isPreview && !(formik.values.audiocredit.length > 0)) && (
            <div className="col-sm-12 col-md-4">
              <CustomSelect
                label="Audio Credit"
                value={processPermissionForSelect(formik.values.audiocredit)}
                handleChange={(newValue: any) => {
                  handlePermissionChange(
                    newValue.map((option: any) => option.value),
                    "audiocredit"
                  );
                  store.dispatch(getCreditList({ name: "", key: "audio" }));
                }}
                onInputChangeCallback={(params) => {
                  if (params.keyword.length >= 3) {
                    store.dispatch(
                      getCreditList({ name: params.keyword, key: "audio" })
                    );
                  } else {
                    store.dispatch(getCreditList({ name: "", key: "audio" }));
                  }
                }}
                options={audioCreditList?.list?.map((val: any) => {
                  return {
                    label: ` ${val?.name}  ${
                      val?.email ? "(" + truncateText(val?.email, 20) + ")" : ""
                    } `,
                    value: val?.id,
                  };
                })}
                handleBlur={formik.handleBlur}
                multiSelect={true}
                touched={formik.touched.audiocredit as boolean}
                hideArrow={true}
                updatedOptions={audioCreditList?.list?.map((val: any) => {
                  return {
                    label: ` ${val?.name}  ${
                      val?.email ? "(" + truncateText(val?.email, 20) + ")" : ""
                    } `,
                    value: val?.id,
                  };
                })}
                creditData={selectedCredits}
                checkBox={false}
                loader={true}
                // disabled={!enabledFields.audiocredit}
                showRequired={false}
                error={formik.errors.audiocredit}
                showAsterisk={
                  !!selectedMedia?.credits?.audio_credits_mode ||
                  !!item?.credits?.audio_credits_mode
                }
                asteriskClassName={getModeDotClass(
                  selectedMedia?.credits?.audio_credits_mode ||
                    item?.credits?.audio_credits_mode ||
                    ""
                )}
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.audiocredit}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle("audiocredit", checked);
                }}
                disabled={isPreview || !enabledFields.audiocredit}
                disableLabelCheckbox={
                  isPreview && !(formik.values.audiocredit.length > 0)
                }
                // showAsterisk={currentMediaDetailsForEdit?.audiocredit?.length > 0 && enabledFields.audiocredit}
                readOnlyMode={readOnlyMode}
                customStyling={
                  isPreview ? { border: "none", marginLeft: "-10px" } : {}
                }
              />
            </div>
          )}
        </div>
        <div className="row gy-3 m-0">
          {shouldShowGeoRestriction() && (
            <h6 className="mt-4 mb-2">Geo Restriction</h6>
          )}
          {shouldShowField(formik.values.gct, !!isPreview) && (
            <div className="col-sm-12 col-md-4">
              <CustomSelect
                value={formik.values.gct}
                label="Mode"
                options={[
                  { label: "Allowed Countries", value: 1 },
                  { label: "Blocked Countries", value: 2 },
                ]}
                placeholder="Mode"
                handleChange={(newValue: any) => {
                  formik.setFieldValue("gct", newValue.value);
                }}
                hideArrow={true}
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.gct}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle("gct", checked);
                }}
                disabled={!!isPreview || !enabledFields.gct}
                showAsterisk={
                  !!currentMediaDetailsForEdit?.gct && enabledFields.gct
                }
                disableLabelCheckbox={!!isPreview && !formik.values.gct}
                readOnlyMode={readOnlyMode}
                customStyling={
                  isPreview ? { border: "none", marginLeft: "-10px" } : {}
                }
              />
            </div>
          )}
          {shouldShowField(formik.values.gcl, !!isPreview) && (
            <div className="col-sm-12 col-md-4">
              <CustomSelect
                label="Countries"
                options={COUNTRIES}
                placeholder="Countries"
                handleChange={(newValue: any) => {
                  const newCountries = [
                    ...new Set(newValue.map((v: any) => v.value)),
                  ];
                  formik.setFieldValue("gcl", newCountries);
                }}
                value={formik.values.gcl}
                multiSelect={true}
                handleClear={() => formik.setFieldValue("gcl", [])}
                updatedOptions={COUNTRIES}
                hideArrow={true}
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.gcl}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle("gcl", checked);
                }}
                disabled={!!isPreview || !enabledFields.gcl}
                showAsterisk={
                  currentMediaDetailsForEdit?.gcl?.length > 0 &&
                  enabledFields.gcl
                }
                disableLabelCheckbox={
                  !!isPreview && !(formik.values.gcl.length > 0)
                }
                readOnlyMode={readOnlyMode}
                customStyling={
                  isPreview ? { border: "none", marginLeft: "-10px" } : {}
                }
              />
            </div>
          )}
        </div>
        <div className="row gy-3 m-0">
          {shouldShowField(formik.values.expiry, !!isPreview) && (
            <div className="col-sm-12 col-md-4">
              <label className="d-flex align-items-center gap-2 mb-1">
                <input
                  type="checkbox"
                  checked={enabledFields.expiry}
                  onChange={(e) =>
                    handleFieldToggle("expiry", e.target.checked)
                  }
                  disabled={!!isPreview && !formik.values.expiry}
                  className="align-middle"
                />
                <span style={{ fontSize: "12px" }}>Expiry</span>
              </label>
              <div style={{ marginTop: "-23px" }}>
                <UnifiedDateTimePicker
                  mode="date"
                  name="expiry"
                  onChange={(date: Date | null) => {
                    formik.setFieldValue("expiry", date ? date.getTime() : 0);
                  }}
                  value={formik.values.expiry}
                  disabled={!!isPreview || !enabledFields.expiry}
                  showRequired={
                    currentMediaDetailsForEdit?.expiry > 0 &&
                    enabledFields.expiry
                  }
                />
              </div>
            </div>
          )}
          {shouldShowField(formik.values.youtube_id, !!isPreview) && (
            <div className="col-sm-12 col-md-4">
              <TextInput
                label="Youtube Id"
                value={formik.values.youtube_id}
                onChange={(e) =>
                  formik.setFieldValue("youtube_id", e.target.value)
                }
                placeholder="Enter Id"
                labelCheckbox={true}
                labelCheckboxChecked={enabledFields.youtube_id}
                onLabelCheckboxChange={(checked: boolean) => {
                  handleFieldToggle("youtube_id", checked);
                }}
                disabled={!!isPreview || !enabledFields.youtube_id}
                showRequired={
                  !!currentMediaDetailsForEdit?.youtube_id &&
                  enabledFields.youtube_id
                }
                disableLabelCheckbox={!!isPreview && !formik.values.youtube_id}
                readOnlyMode={readOnlyMode}
              />
            </div>
          )}
          {shouldShowField(formik.values.parentid, !!isPreview) && (
            <div className="col-sm-12 col-md-4">
              <label className="d-flex align-items-center gap-2 mb-1">
                <input
                  type="checkbox"
                  checked={enabledFields.parentid}
                  onChange={(e) =>
                    handleFieldToggle("parentid", e.target.checked)
                  }
                  disabled={!!isPreview && !formik.values.parentid}
                  className="align-middle"
                />
                <span style={{ fontSize: "12px" }}>Parent Video</span>
              </label>
              {selectedParentVideo ? (
                <SelectedMediaCard
                  media={selectedParentVideo}
                  type="pre"
                  onClose={() => {
                    formik.setFieldValue("parentid", "");
                    setSelectedParentVideo(null);
                  }}
                  showEditButton={false}
                  showDeleteButton={!isPreview && !readOnlyMode}
                  showPreviewButton={true}
                  cardStyle={{ width: "80px", minWidth: "80px" }}
                  style={{ width: "220px" }}
                  maxLength={20}
                  readOnlyMode={readOnlyMode}
                />
              ) : (
                <TextInput
                  value={formik.values.parentid}
                  onChange={(e) =>
                    formik.setFieldValue("parentid", e.target.value)
                  }
                  placeholder="Search Title, Id"
                  disabled={!!isPreview || !enabledFields.parentid}
                  readOnlyMode={readOnlyMode}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {isPreview && isMediaSource && mediaSourceThumbs.length > 0 && (
        <div className="mt-3">
          <label className="mb-2">Thumbnails</label>
          <div className="d-flex flex-wrap gap-3">
            {mediaSourceThumbs.map((thumb) => {
              const isChecked = !excludedThumbTypes.has(thumb.type);
              return (
                <div key={thumb.type}>
                  <label className="d-flex align-items-center gap-2 mb-1">
                    {!readOnlyMode && (
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setExcludedThumbTypes((prev) => {
                            const next = new Set(prev);
                            if (checked) {
                              next.delete(thumb.type);
                            } else {
                              next.add(thumb.type);
                            }
                            onThumbToggle?.(next);
                            return next;
                          });
                        }}
                        className="align-middle"
                      />
                    )}
                    <span
                      style={{ fontSize: "12px", textTransform: "capitalize" }}
                    >
                      {thumb.type}
                    </span>
                  </label>
                  <div
                    className="rounded ratio ratio-16x9 border bg-secondary"
                    style={{ width: "160px" }}
                  >
                    <img
                      src={thumb.url || noImage}
                      alt={`${thumb.type} thumbnail`}
                      className="object-fit-cover position-absolute top-50 start-50 translate-middle rounded"
                      style={{ width: "100%", height: "100%" }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};
const mapState = (state: RootState) => ({
  categories: state.media.categoryList,
  bunchCategoryList: state.constants.bunchCategoryList.list,
  bunchLanguageList: state.constants.bunchLanguageList.list,
  tag: state.app.tag,
  cast: state.app.cast,
  genre: state.app.genre,
  mostFrequentUsers: state.users.mostFrequentUsers,
  mostFrequentEditor: state.users.mostFrequentEditor,
  mostFrequentScriptWriter: state.users.mostFrequentScriptWriter,
  mostFrequentAnchor: state.users.mostFrequentAnchor,
  mostFrequentCameraman: state.users.mostFrequentCameraman,
  mostFrequentContentType: state.users.mostFrequentContentType,
  mostFrequentCategory: state.users.mostFrequentCategory,
  userList: state.users.userList.list,
  creditList: state.media.creditList,
  audioCreditList: state.media.audioCreditList,
  videoCreditList: state.media.videoCreditList,
  imageCreditList: state.media.imageCreditList,
  presetList: state.media.presetList.list,
  liveList: state.media.liveList.list,
  bunchProductList: state.constants.bunchProductList.list,
  bunchBusinessProductMap: state.constants.bunchBusinessProductMap,
  defaultTeam: state.constants.defaultTeam,
});

const mapDispatch = {
  getMostFrequentAll,

  getListUser,
  getListAgencies,
};

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector> & RenderCreateMetaPresetProps;
export default connector(CreateMetaPreset);
